import axios, { AxiosInstance, CanceledError } from 'axios';
import FormData from 'form-data';
import { capitalize } from 'vue';

import EntityType from '@/common/enums/EntityType.enum';
import Environment from '@/common/enums/environment.enum';
import HeatmapType from '@/common/enums/heatmap-type.enum';
import Advertiser from '@/common/models/advertisers/Advertiser';
import UniqueAdvertiser from '@/common/models/advertisers/UniqueAdvertiser';
import AIThread from '@/common/models/ai/AIThread';
import AudienceRecommendation from '@/common/models/ai/AudienceRecommendation';
import Announcement from '@/common/models/announcements/Announcement';
import AttachableFee from '@/common/models/AttachableFee';
import { AudienceConcentrationGroupings } from '@/common/models/audience/AudienceConcentrationGroupings';
import { AudienceConcentrationPerUnit } from '@/common/models/audience/AudienceConcentrationPerUnit';
import AudienceLogic from '@/common/models/audience/AudienceLogic';
import AudienceRuleCustomGroup from '@/common/models/audience/AudienceRuleCustomGroup';
import ExternalSegment from '@/common/models/audience/ExternalSegment';
import MastercardAudienceLogic from '@/common/models/audience/MastercardAudienceLogic';
import { ReachStatistics } from '@/common/models/audience/ReachStatistics';
import AudienceFile from '@/common/models/audienceFiles/AudienceFile';
import AuditTrail from '@/common/models/audit/AuditTrail';
import AvailsProgress from '@/common/models/avails/AvailsProgress';
import AvailsImport from '@/common/models/availsImport/AvailsImport';
import Campaign from '@/common/models/campaign/Campaign';
import SharedCampaign from '@/common/models/campaign/SharedCampaign';
import Creative from '@/common/models/creative/Creative';
import CreativeFile from '@/common/models/creative/CreativeFile';
import CreativeMediaApproval from '@/common/models/creative/CreativeMediaApproval';
import CreativeProperty from '@/common/models/creative/CreativeProperty';
import CreativeUnitDimensions from '@/common/models/creative/CreativeUnitDimensions';
import CustomEvent from '@/common/models/customEvents/CustomEvent';
import DeliveryForecastProgress from '@/common/models/deliveryForecast/DeliveryForecastProgress';
import CuratedPackageDeliveryStatistics from '@/common/models/deliveryStatistics/CuratedDeliveryStatistics';
import CurateUnitCo2eEmissionsByVenueType from '@/common/models/deliveryStatistics/CurateUnitCo2eEmissionsByVenueType';
import DeliveryStatistics from '@/common/models/deliveryStatistics/DeliveryStatistics';
import ReachAndFrequencyStatistics from '@/common/models/deliveryStatistics/ReachAndFrequencyStatistics';
import Demographic from '@/common/models/demographics/Demographic';
import ExternalDemographic from '@/common/models/demographics/ExternalDemographic';
import EntityFee from '@/common/models/EntityFee';
import FeatureFees from '@/common/models/FeatureFees';
import { FillRate } from '@/common/models/FillRate';
import GenericGeofenceTypeCountry from '@/common/models/geo/GenericGeofenceTypeCountry';
import GeocodedLocation from '@/common/models/geo/GeocodedLocation';
import Geofence from '@/common/models/geo/Geofence';
import GeofenceGroup from '@/common/models/geo/GeofenceGroup';
import Location from '@/common/models/geo/Location';
import Market from '@/common/models/geo/Market';
import HeaderBidderSettings from '@/common/models/headerBidder/HeaderBidderSettings';
import JourneyAnalysis from '@/common/models/journeyAnalysis/JourneyAnalysis';
import JourneyPostResponse from '@/common/models/journeyAnalysis/JourneyPostResponse';
import OldJourneyAnalysis from '@/common/models/journeyAnalysis/OldJourneyAnalysis';
import LiftAnalysisData from '@/common/models/lift-dashboard/LiftAnalysisData';
import LiftAnalysisSummary from '@/common/models/lift-dashboard/LiftAnalysisSummary';
import LiftDashboardApiModel from '@/common/models/lift-dashboard/LiftDashboardApiModel';
import LineItem from '@/common/models/lineitem/LineItem';
import LineItemCreative from '@/common/models/lineitem/LineItemCreative';
import LineItemDelivery from '@/common/models/lineitem/LineItemDelivery';
import LineItemDemographic from '@/common/models/lineitem/LineItemDemographic';
import LineItemMediaApproval from '@/common/models/lineitem/LineItemMediaApproval';
import LineItemStatus from '@/common/models/lineitem/LineItemStatus.enum';
import Measurement from '@/common/models/measurements/Measurement';
import OrganizationMeasurement from '@/common/models/measurements/OrganizationMeasurement';
import Membership from '@/common/models/memberships/Membership';
import MembershipSettings from '@/common/models/memberships/MembershipSettings';
import Organization from '@/common/models/memberships/Organization';
import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
import Permissions from '@/common/models/memberships/Permissions';
import Account from '@/common/models/misc-api-models/Account';
import AdServerPlay from '@/common/models/misc-api-models/AdServerPlay';
import DataMarketplace from '@/common/models/misc-api-models/DataMarketplace';
import DataProvider from '@/common/models/misc-api-models/DataProvider';
import DiscountType from '@/common/models/misc-api-models/DiscountType';
import ExternalExchange from '@/common/models/misc-api-models/ExternalExchange';
import ExternalSSP from '@/common/models/misc-api-models/ExternalSSP';
import Language from '@/common/models/misc-api-models/Language';
import MediaType from '@/common/models/misc-api-models/MediaType';
import Network from '@/common/models/misc-api-models/Network';
import OrganizationDSP from '@/common/models/misc-api-models/OrganizationDSP';
import MobileLineItem from '@/common/models/mobilelineitem/MobileLineItem';
import OrganizationCustomField from '@/common/models/organization-custom-fields/OrganizationCustomField';
import OrganizationCustomFieldValue from '@/common/models/organization-custom-fields/OrganizationCustomFieldValue';
import OwnerYieldManagerSsp from '@/common/models/ownerYieldManagerSsp/OwnerYieldManagerSsp';
import DealDiscoveryStatistics from '@/common/models/package/DealDiscovery';
import Package from '@/common/models/package/Package';
import PackageType from '@/common/models/package/PackageType.enum';
import SpendSteeringSummary from '@/common/models/package/SpendSteeringSummary';
import PackageGroup from '@/common/models/package-groups/PackageGroup';
import Plan from '@/common/models/plan/Plan';
import PlanApproval from '@/common/models/plan/PlanApproval';
import PlanBreakdownData from '@/common/models/plan/PlanBreakdownData';
import SharedPlan from '@/common/models/plan/SharedPlan';
import PostalCodesFile from '@/common/models/postalCodesFiles/PostalCodesFile';
import PreSaleHeatmapApiModel from '@/common/models/presale-heatmap/PreSaleHeatmapApiModel';
import { FilterComparisonOperator } from '@/common/models/query-params';
import Filter from '@/common/models/query-params/Filter';
import FilterCollection from '@/common/models/query-params/FilterCollection';
import QueryParams from '@/common/models/query-params/QueryParams';
import AdvancedReportExecution from '@/common/models/reports/AdvancedReportExecution';
import AdvancedReportSchedule from '@/common/models/reports/AdvancedReportSchedule';
import ReachAndFrequencyReport from '@/common/models/reports/ReachAndFrequencyReport';
import ReportDefinition from '@/common/models/reports/ReportDefinition';
import ReportExecution from '@/common/models/reports/ReportExecution';
import ReportSchedule from '@/common/models/reports/ReportSchedule';
import SummarizedReport from '@/common/models/reports/SummarizedReport';
import RevenueSplit from '@/common/models/revenueSplit/RevenueSplit';
import SharedPublicEntity from '@/common/models/SharedPublicEntity';
import Site from '@/common/models/site/Site';
import SiteInfo from '@/common/models/site/SiteInfo';
import OrganizationSubscription from '@/common/models/subscriptions/OrganizationSubscription';
import TargetingCriterion from '@/common/models/targeting-criteria/TargetingCriterion';
import Unit from '@/common/models/unit/Unit';
import UnitDimension from '@/common/models/unit/UnitDimension';
import UnitFallbackCreative from '@/common/models/unit/UnitFallbackCreative';
import UnitInfo from '@/common/models/unit/UnitInfo';
import UnitPack from '@/common/models/unit/UnitPack';
import UnitPackInventoryTargeting from '@/common/models/unit/UnitPackInventoryTargeting';
import Role from '@/common/models/user/Role';
import User from '@/common/models/user/User';
import UserOrganization from '@/common/models/user/UserOrganization';
import UserSetting from '@/common/models/user/UserSetting';
import YieldManagerBidStats from '@/common/models/yieldManager/YieldManagerBidStats';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { environment, HttpStatusCodeNoResponse, toServerDate, toServerDateTimeNoTimezone } from '@/globals';
import rootState from '@/store/state';
import ApiUtils from '@/utils/ApiUtils';
import LocalCache from '@/utils/LocalCache';
import i18n from '@/vueI18n';
import { DateTime } from 'luxon';

export const BaseApiUrl = '/api/ui/org/';

export const accountsEndpoint = 'accounts/';
export const advancedReportExecutionsEndpoint = 'customreportexecutions';
export const advancedReportSchedulesEndpoint = 'customreportschedules/';
export const adrequestsEndpoint = 'adrequests/';
export const advertisersEndpoint = 'adv/'; // There's an alias for /advertisers because ad-blockers
export const advertisersWithBuyersEndpoint = 'adv/withbuyer'; // There's an alias for /advertisers because ad-blockers
export const alertsEndpoint = 'alerts/';
export const announcementsEndpoint = 'announcements/';
export const attachableFeesEndpoint = 'fees/attachablefeecategories/';
export const attachedMeasurementsEndpoint = 'attachedmeasurements/';
export const audienceConcentration = 'audiencedata/forperiod/concentrations/groupings/';
export const audienceDataEndpoint = 'audiencedata/ingestaudiencefile/';
export const audienceDeviceIDGroupEndpoint = 'customgroups/';
export const audienceEndpoint = 'audience/';
export const audienceFilesEndpoint = 'audiencefiles/';
export const audienceLogicsEndpoint = 'audiencelogics/';
export const audienceLogicsMastercardEndpoint = 'mastercard/audiencelogics/';
export const availsEndpoint = 'avails/';
export const availsImportEndpoint = 'importedavailsfiles/';
export const buildingsEndpoint = 'buildings/';
export const buyersEndpoint = 'organizations/buyers/';
export const campaignsEndpoint = 'campaigns/';
export const creativeFilesEndpoint = 'creativefiles/';
export const creativeMediaOwnerApprovalsEndpoint = 'creativemediaownerapprovals/';
export const creativePrefetchSizeEndpoint = 'creativeprefetchsizes/';
export const creativePropertiesEndpoint = 'creativeproperties/';
export const creativesEndpoint = 'creatives/';
export const curatedPackagesEndpoint = 'curate/packages/';
export const curatedPackagesEstimatedDeliveryStatsEndpoint = `${curatedPackagesEndpoint}estimateddeliverystatistics`;
export const curateUnitCo2eEmissionsEndpoint = 'curate/unitco2eemissions/';
export const curatorsEndpoint = 'organizations/curators/';
export const curatorSuppliersEndpoint = 'curate/suppliers/';
export const currentUserEndpoint = 'users/current';
export const customEventsEndpoint = '/customevents/';
export const dailyAndAverageConcentrationPerUnitEndpoint = 'audiencedata/forperiod/dailyconcentrations/';
export const dataProvidersEndpoint = 'dataproviders/';
export const dataMarketplaceEndpoint = 'datamarketplaces/';
export const dealDiscoveryEndpoint = 'dealdiscovery';
export const deliveryForecastEndpoint = `${availsEndpoint}deliveryforecastexecution`;
export const deliveryStatisticsEndpoint = 'deliverystatistics/estimate';
export const deliveryStatisticsScreenAndSiteCount = 'deliverystatistics/count';
export const deliveryStatisticsTargetedIDsEndpoint = 'deliverystatistics/targetedids';
export const deliveryStatisticsUnitsMissingAudienceDataBriefEndpoint =
  'deliverystatistics/units-missing-audiencedata/brief';
export const deliveryStatisticsUnitsMissingAudienceDataCountEndpoint =
  'deliverystatistics/units-missing-audiencedata/count';
export const demographicsEndpoint = 'demographics/';
export const discountTypesEndpoint = 'discounttypes/';
export const entityFeesEndpoint = 'fees/entityfees/';
export const estimatedReachEndpoint = 'audiencedata/forperiod/estimatedreach';
export const externalDemographicsEndpoint = 'externaldemographics/';
export const externalPackageGroupsEndpoint = 'externalpackagegroups/';
export const externalSegmentCategoriesByOrganizationEndpoint = 'externalsegments/organization/{id}/categories';
export const externalSegmentsByOrganizationEndpoint = 'externalsegments/organization/';
export const externalSegmentsEndpoint = 'externalsegments/';
export const featureFeesEndpoint = 'feature/values';
export const freshdeskTokenEndpoint = 'users/freshworks/jwt';
export const genericGeofenceEndpoint = 'genericgeofences/';
export const genericGeofenceTypeCountryEndpoint = 'genericgeofencetypecountries/';
export const geofenceGroupsEndpoint = 'geofencegroups/';
export const geofencesEndpoint = 'geofences/';
export const journeyAnalysesEndpoint = 'journeyanalysis/';
export const languagesEndpoint = 'languages/';
export const liftAnalysesEndpoint = 'liftanalyses/';
export const liftDashboardsEndpoint = 'measurementdashboards/';
export const lineItemCreativesEndpoint = 'lineitemcreatives/';
export const lineItemDemographicTargetsEndpoint = 'lineitemdemographictargets/';
export const lineItemMediaOwnerApprovalsEndpoint = 'lineitemsmediaownerapprovals/';
export const lineItemsEndpoint = 'lineitems/';
export const localJourneyAnalysesEndpoint = 'journeyAnalysesOld';
export const locationsEndpoint = 'locations/';
export const loginEndpoint = 'login/';
export const marketsEndpoint = 'markets/';
export const measurementsEndpoint = 'organizationmeasurements/';
export const mediaOwnersEndpoint = 'organizations/suppliers/';
export const mediaTypesEndpoint = 'mediatypes/';
export const membershipsEndpoint = 'users/memberships/';
export const mobileLineItemsEndpoint = 'mobilelineitems/';
export const networksEndpoint = 'networks/';
export const organizationCustomFieldsEndpoint = 'organizationcustomfields/';
export const organizationCustomFieldsValuesEndpoint = 'organizationcustomvalues/';
export const organizationDSPsEndpoint = 'organizationdsps/';
export const organizationsEndpoint = 'organizations/';
export const organizationSettingsEndpoint = 'organizations/settings/';
export const ownerYieldManagerSspsEndpoint = 'owneryieldmanagerssps/';
export const packageEndpoint = 'package/';
export const packageGroupsEndpoint = 'packagegroups/';
export const packagesEndpoint = 'packages/';
export const packagesToLineitemsEndpoint = 'converttolineitems/';
export const passwordResetEmailEndpoint = 'users/requestpasswordreset/';
export const permissionsEndpoint = 'permissions/';
export const planApprovalsEndpoint = 'plans/shared/approvals/';
export const plansBreakdownDataEndpoint = 'plans/breakdowndata';
export const plansEndpoint = 'plans/';
export const postalCodesFilesEndpoint = 'postalcodesfiles/';
export const preSaleHeatmapsEndpoint = 'presaleheatmaps/';
export const reachAndFrequencyOverall = 'geopath/reachandfrequency/overall';
export const reachAndFrequencyReportEndpoint = 'reachandfrequency';
export const refreshTokenEndpoint = 'refreshtoken/';
export const reportDefinitionsEndpoint = 'reportdefinitions/';
export const reportExecutionsEndpoint = 'reportexecutions/';
export const reportSchedulesEndpoint = 'reportschedules/';
export const reportsEndpoint = 'reports/';
export const resetPasswordEndpoint = 'users/resetpassword/';
export const revenueSplit = 'revenuesplit/';
export const rolesEndpoint = 'roles/';
export const sendVerificationEmailEndpoint = 'userorganizations/requestemailvalidation/';
export const sitesEndpoint = 'sites/';
export const sitesNetworkEndpoint = 'sites/networksclustered';
export const subscriptionsEndpoint = 'subscriptions/';
export const tagsEndpoint = 'tags/';
export const targetingCriteriaEndpoint = 'targetingcriteria/';
export const themingEndpoint = 'theming/';
export const TTDAdvertisersEndpoint = 'ttd/advertisers/';
export const uniqueAdvertiserEndpoint = 'uniqueadv/';
export const uniqueAdvertiserWithBuyersEndpoint = 'uniqueadv/withbuyer';
export const unitFallbackCreativesEndpoint = 'unitfallbackcreatives/';
export const unitPacksEndpoint = 'unitpacks/';
export const unitPropertiesEndpoint = 'unitproperties/';
export const unitsEndpoint = 'units/';
export const unitsHourlyImpressionsEndpoint = 'audiencedata/forperiod/hourlyimpressions/';
export const userOrganizationsEndpoint = 'userorganizations/';
export const usersEndpoint = 'users/';
export const userSettingsEndpoint = 'useruisettings/';
export const verifyEmailEndpoint = 'users/verifyemail/';
export const yieldManagerEndpoint = 'yieldmanager/';
export const externalSSPsEndpoint = 'externalssps/';

const getStatusCode = (error) =>
  error != null && error.response != null ? error.response.status : HttpStatusCodeNoResponse;

const getErrorOld = (error) => {
  if (error === null || error === undefined || error.response === null || error.response === undefined) {
    return 'unspecified error';
  }

  if (
    error.response.data !== undefined &&
    error.response.data.name !== undefined &&
    error.response.data.name.length > 0
  ) {
    return error.response.data.name.join('<br>');
  }
  if (
    error.response.data !== undefined &&
    error.response.data.error_code !== undefined &&
    error.response.data.error_code !== null
  ) {
    const errorKey = `errors.e_${error.response.data.error_code.replace(/-/g, '_')}`;
    const errorText = i18n.global.t(errorKey);
    if (errorText !== undefined && errorText !== errorKey) {
      return capitalize(errorText);
    }
    if (error.response.data.error !== undefined) {
      return ` - ${error.response.data.error}`;
    }
  }
  if (error.response.data !== undefined) {
    return error.response.data;
  }
  return error.message;
};

const handleError = (errorResponse, errorCallback) => {
  errorCallback(errorResponse);
  console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
};

//
// Query parameters transforms.
//
const userOrganizationParamsTransform = (qp) => {
  qp.expand = [...(qp.expand || []), 'user', 'role'];
  return qp;
};

const creativeApprovalQueryParamsTransformation = (qp) => {
  qp.expand = ['creative.inventory_targeting', 'inventory_targeting'];
  return qp;
};

const uniqueAudienceLogicParamsTransform = (qp) => {
  qp.expand = [...(qp.expand || []), 'audiencerules.audiencelookbackwindows'];
  return qp;
};

const mobileLineItemParamsTransform = (qp) => {
  qp.expand = [
    ...(qp.expand || []),
    'mobilelineitem_creatives',
    'mobilelineitem_lineitems',
    'campaign',
    'campaign.advertiser',
    'campaign.account',
  ];
  return qp;
};

const reportDefinitionParamsTransform = (qp) => {
  qp.expand = [...(qp.expand || []), 'report_schedules'];
  return qp;
};

const reportScheduleParamsTransform = (qp) => {
  qp.expand = [...(qp.expand || []), 'report_schedules'];
  return qp;
};

const uniqueAdvertiserParamsTransform = (qp) => {
  qp.expand = [...(qp.expand || []), 'advertiser_domains'];
  return qp;
};

const uniqueCampaignParamsTransform = (qp) => {
  qp.expand = [
    ...(qp.expand || []),
    'advertiser.unique_advertiser.replacement_unique_advertiser.advertiser_domains',
    'campaign_properties',
    'cachedpacing',
    'margins',
    'campaign_internal_properties',
    'shared_entity',
    'flights',
  ];
  return qp;
};

const uniqueReportExecutionParamsTransform = (qp) => {
  qp.expand = [...(qp.expand || []), 'report_definition'];
  return qp;
};

const uniqueCreativeParamsTransform = (qp) => {
  qp.expand = [
    ...(qp.expand || []),
    'creative_locationtargets',
    'creative_locationexclusions',
    'creative_unitsiteassignments',
    'creative_geofencegroups',
    'creative_networks',
    'creative_networkexclusions',
    'creative_mediatypes',
    'creative_mediatypeexclusions',
    'creative_markets',
    'creative_marketexclusions',
    'creative_targetingcriteria/creativetargetingcriterion_values',
    'creative_trackingurls',
    'creative_locationexclusions',
    'creative_unitsiteexclusions',
    'advertiser.unique_advertiser.replacement_unique_advertiser.advertiser_domains',
    'creative_creativeformats',
    'creative_creativeformatexclusions',
    'inventory_targeting',
    'precise_daypart',
    'mediaowner_approvals.inventory_targeting',
  ];
  return qp;
};

const apiInstance = null;

class APIProxy {
  //
  // Static members
  //
  static unitFiltersForTargetsCache = [];
  static typeFiltersForTargetsCache = '';

  private axiosInstanceInternal: AxiosInstance;
  private axiosInstancePublicEndpoints: AxiosInstance;
  private apiUtils: ApiUtils = new ApiUtils(() => this.axiosInstance);
  private refreshSubscribers = [];
  private isRefreshingToken = false;

  constructor() {
    this.apiUtils = new ApiUtils(() => this.axiosInstance);
    this.axiosInstancePublicEndpoints = axios.create({ baseURL: BaseApiUrl });
  }

  //
  // Measurements
  //
  public getMeasurements = this.apiUtils.createGetAllFunction(measurementsEndpoint, OrganizationMeasurement);
  public getMeasurementDetails = this.apiUtils.createGetAllFunction(
    `${measurementsEndpoint}withdetails/{organizationID}`,
    Measurement,
    null,
    null,
    null,
    (url, params) => url.replace('{organizationID}', params.customQueryParams.organizationID),
  );
  public getAttachedMeasurements = this.apiUtils.createGetAllFunction(
    attachedMeasurementsEndpoint,
    OrganizationMeasurement,
  );

  //
  // Entity Fees
  //
  public getAttachableFees = this.apiUtils.createApiFunction(attachableFeesEndpoint, 'get', AttachableFee, true);
  public getEntityFees = this.apiUtils.createApiFunction(entityFeesEndpoint, 'get', EntityFee, true);

  //
  // Feature Fees
  //
  public getFeatureFees = this.apiUtils.createApiFunction(featureFeesEndpoint, 'post', FeatureFees);

  //
  // Accounts
  //
  public getAccount = this.apiUtils.createGetOneFunction(accountsEndpoint, Account);

  //
  // Announcements
  //
  public getUnreadAnnouncements = this.apiUtils.createGetAllFunction(`${announcementsEndpoint}unread/`, Announcement);
  public markAnnoucementAsRead = this.apiUtils.createApiFunction(
    `${announcementsEndpoint}{id}/read`,
    'post',
    Announcement,
    false,
    null,
    (url, params, data) => url.replace('{id}', data.id),
  );

  // Audience Recommendation
  public getAudienceThread = this.apiUtils.createApiFunction(`${audienceEndpoint}thread/`, 'get', AIThread);
  public getAudienceRecommendation = this.apiUtils.createApiFunction(
    `${audienceEndpoint}recommendations/`,
    'POST',
    AudienceRecommendation,
  );

  //
  // Audience logics
  //
  public getAudienceLogics = this.apiUtils.createGetAllFunction(audienceLogicsEndpoint, AudienceLogic);
  public getAudienceLogic = this.apiUtils.createGetOneFunction(
    audienceLogicsEndpoint,
    AudienceLogic,
    uniqueAudienceLogicParamsTransform,
  );
  public postAudienceLogics = this.apiUtils.createPostFunction(
    `${audienceLogicsEndpoint}allinclusive/`,
    AudienceLogic,
    null,
  );
  public postMastercardAudienceLogics = this.apiUtils.createPostFunction(
    `${audienceLogicsMastercardEndpoint}allinclusive/`,
    MastercardAudienceLogic,
    null,
  );
  public putAudienceLogic = this.apiUtils.createPutFunction(audienceLogicsEndpoint, AudienceLogic, null);
  public deleteAudienceLogic = this.apiUtils.createDeleteFunction(audienceLogicsEndpoint);

  //
  // Audience Device ID Segments / Custom group
  //
  public getAudienceCustomGroups = this.apiUtils.createGetAllFunction(
    audienceDeviceIDGroupEndpoint,
    AudienceRuleCustomGroup,
  );
  public getAudienceCustomGroup = this.apiUtils.createGetOneFunction(
    audienceDeviceIDGroupEndpoint,
    AudienceRuleCustomGroup,
  );
  public postAudienceCustomGroup = this.apiUtils.createPostFunction(
    audienceDeviceIDGroupEndpoint,
    AudienceRuleCustomGroup,
    null,
  );
  public putAudienceCustomGroup = this.apiUtils.createApiFunction(
    `${audienceDeviceIDGroupEndpoint}{id}`,
    'put',
    AudienceRuleCustomGroup,
    false,
    null,
    (url, params) => url.replace('{id}', params.id),
  );

  //
  // External audience segments
  //
  public getAllExternalSegments = this.apiUtils.createGetAllFunction(externalSegmentsEndpoint, ExternalSegment);
  public getAllExternalSegmentsForDataProvider = this.apiUtils.createGetAllFunction(
    `${externalSegmentsEndpoint}`,
    ExternalSegment,
    (params) => {
      params.filterCollection.addFilter(
        new Filter('dataprovider_id', [params.customQueryParams.dataProviderId], FilterComparisonOperator.EQUAL),
      );
      return params;
    },
  );
  public getExternalAudienceSegmentsByOrg = this.apiUtils.createGetAllFunction(
    `${externalSegmentsByOrganizationEndpoint}{id}`,
    ExternalSegment,
    null,
    null,
    null,
    (url, params) => url.replace('{id}', params.customQueryParams.dataProviderId),
  );
  public getExternalCountriesAndCategoriesByOrg = this.apiUtils.createApiFunction(
    externalSegmentCategoriesByOrganizationEndpoint,
    null,
    null,
    false,
    null,
    (url, params) => url.replace('{id}', params.customQueryParams.dataProviderId),
  );

  //
  // Advanced Report schedules
  //
  public getAdvancedReportSchedules = this.apiUtils.createGetAllFunction(
    advancedReportSchedulesEndpoint,
    AdvancedReportSchedule,
  );
  public getAdvancedReportSchedule = this.apiUtils.createGetOneFunction(
    advancedReportSchedulesEndpoint,
    AdvancedReportSchedule,
  );
  public postAdvancedReportSchedules = this.apiUtils.createPostFunction(
    advancedReportSchedulesEndpoint,
    AdvancedReportSchedule,
    null,
  );
  public putAdvancedReportSchedule = this.apiUtils.createPutFunction(
    advancedReportSchedulesEndpoint,
    AdvancedReportSchedule,
    null,
  );
  public deleteAdvancedReportSchedule = this.apiUtils.createDeleteFunction(advancedReportSchedulesEndpoint);

  //
  // Advertisers
  //
  public getAdvertisersWithBuyer = this.apiUtils.createGetAllFunction(
    advertisersWithBuyersEndpoint,
    Advertiser,
    (qp) => {
      qp.expand = [...(qp.expand || []), 'unique_advertiser.advertiser_domains'];
      return qp;
    },
  );

  //
  // Avails Import
  //
  public getAvailsImport = this.apiUtils.createGetAllFunction(`all${availsImportEndpoint}`, AvailsImport);

  //
  // Campaigns
  //
  public getCampaigns = this.apiUtils.createGetAllFunction(campaignsEndpoint, Campaign);
  public getCampaign = this.apiUtils.createGetOneFunction(campaignsEndpoint, Campaign, uniqueCampaignParamsTransform);
  public getPublicSharedCampaign = this.apiUtils.createApiFunction(
    `${campaignsEndpoint}shared/`,
    'post',
    SharedCampaign,
  );
  public postCampaigns = this.apiUtils.createPostFunction(campaignsEndpoint, Campaign, null);
  public putCampaign = this.apiUtils.createPutFunction(campaignsEndpoint, Campaign, null);
  public deleteCampaign = this.apiUtils.createDeleteFunction(campaignsEndpoint);
  public getCampaignAudit = this.apiUtils.createGetAuditFunction(campaignsEndpoint, 'Campaign');
  public extendPublicCampaign = this.apiUtils.createApiFunction(
    'campaigns/{campaignID}/extendsharedexpiry',
    'post',
    null,
    false,
    null,
    (url, _, data) => url.replace('{campaignID}', data.id),
  );
  public getCampaignSummaryDeliveryStats = this.apiUtils.createApiFunction(
    `${reportsEndpoint}dashboard/campaign/`,
    'get',
  );
  public getCampaignCustomFields = this.apiUtils.createApiFunction(`${campaignsEndpoint}customfields/`, 'get');
  public getCampaignSummaryPanel = this.apiUtils.createApiFunction(
    'dashboards/campaigns/{id}/summary{start}{end}?timezone=UNIT_LOCAL',
    'get',
    null,
    false,
    null,
    (url, params, data) => {
      let newURL = url;
      newURL = newURL.replace('{id}', data.id);
      newURL = newURL.replace('{start}', data.start);
      newURL = newURL.replace('{end}', data.end);
      return newURL;
    },
  );
  public getPublicSharedCampaignDelivery = this.apiUtils.createApiFunction(
    'dashboards/campaigns/shared/{uuid}/delivery{start}{end}?timezone={timezone}',
    'post',
    null,
    false,
    null,
    (url, params, data) => {
      let newURL = url;
      newURL = newURL.replace('{uuid}', data.uuid);
      newURL = newURL.replace('{start}', data.start);
      newURL = newURL.replace('{end}', data.end);
      newURL = newURL.replace('{timezone}', data.timezone);
      return newURL;
    },
  );
  public getCampaignDetails = this.apiUtils.createApiFunction(
    'dashboards/campaigns/{id}/delivery{start}{end}?timezone={timezone}',
    'get',
    null,
    false,
    null,
    (url, params, data) => {
      let newURL = url;
      newURL = newURL.replace('{id}', data.id);
      newURL = newURL.replace('{start}', data.start);
      newURL = newURL.replace('{end}', data.end);
      newURL = newURL.replace('{timezone}', data.timezone);
      return newURL;
    },
  );
  public getCampaignSites = this.apiUtils.createApiFunction(
    '/dashboards/campaigns/{id}/summary/sites{start}{end}?timezone=UNIT_LOCAL',
    'get',
    null,
    false,
    null,
    (url, params, data) => {
      if (data.path) return data.path.replace('api/ui/org/', '');
      let newURL = url;
      newURL = newURL.replace('{id}', data.id);
      newURL = newURL.replace('{start}', data.start);
      newURL = newURL.replace('{end}', data.end);
      return newURL;
    },
  );
  public getCampaignUnits = this.apiUtils.createApiFunction(
    '/dashboards/campaigns/{id}/summary/units{start}{end}?timezone=UNIT_LOCAL',
    'get',
    null,
    false,
    null,
    (url, params, data) => {
      if (data.path) return data.path.replace('api/ui/org/', '');
      let newURL = url;
      newURL = newURL.replace('{id}', data.id);
      newURL = newURL.replace('{start}', data.start);
      newURL = newURL.replace('{end}', data.end);
      return newURL;
    },
  );

  //
  // Creatives
  //
  public getCreatives = this.apiUtils.createGetAllFunction(creativesEndpoint, Creative);
  public getCreative = this.apiUtils.createGetOneFunction(creativesEndpoint, Creative, uniqueCreativeParamsTransform);
  public postCreatives = this.apiUtils.createPostFunction(creativesEndpoint, Creative);
  public putCreative = this.apiUtils.createPutFunction(creativesEndpoint, Creative);
  public deleteCreative = this.apiUtils.createDeleteFunction(creativesEndpoint);
  public getCreativeAudit = this.apiUtils.createGetAuditFunction(creativesEndpoint, 'Creative');
  public patchCreatives = this.apiUtils.createPatchFunction(creativesEndpoint);

  //
  // Creative media owner approvals
  //
  public getCreativeMediaOwnerApprovals = this.apiUtils.createGetAllFunction(
    creativeMediaOwnerApprovalsEndpoint,
    CreativeMediaApproval,
  );
  public getCreativeMediaOwnerApprovalInventoryTargeting = this.apiUtils.createGetOneFunction(
    creativeMediaOwnerApprovalsEndpoint,
    CreativeMediaApproval,
    creativeApprovalQueryParamsTransformation,
  );
  public getCreativeMediaOwnerApprovalTargetedNetworks = this.apiUtils.createApiFunction(
    `${creativeMediaOwnerApprovalsEndpoint}{id}/targetednetworks`,
    'get',
    null,
    false,
    null,
    (url, params) => url.replace('{id}', params.id),
  );
  public convertCreativeMediaOwnerApproval = this.apiUtils.createApiFunction(
    `${creativeMediaOwnerApprovalsEndpoint}{id}/convert`,
    'post',
    null,
    false,
    null,
    (url, params) => url.replace('{id}', params.id),
  );
  public putCreativeMediaOwnerApprovals = this.apiUtils.createApiFunction(
    `${creativeMediaOwnerApprovalsEndpoint}{id}`,
    'put',
    CreativeMediaApproval,
    false,
    null,
    (url, params) => url.replace('{id}', params.id).replace('{creativeID}', params.creativeID),
  );

  //
  // Curated Packages
  //
  public getCuratedPackages = this.apiUtils.createGetAllFunction(curatedPackagesEndpoint, Package);
  public getCuratedPackage = this.apiUtils.createGetOneFunction(curatedPackagesEndpoint, Package);
  public postCuratedPackages = this.apiUtils.createPostFunction(curatedPackagesEndpoint, Package);
  public putCuratedPackage = this.apiUtils.createPutFunction(curatedPackagesEndpoint, Package);
  public deleteCuratedPackage = this.apiUtils.createDeleteFunction(curatedPackagesEndpoint);

  //
  // Demographics
  //
  public getDemographics = this.apiUtils.createGetAllFunction(demographicsEndpoint, Demographic);
  public getDemographic = this.apiUtils.createGetOneFunction(demographicsEndpoint, Demographic);
  public postDemographics = this.apiUtils.createPostFunction(demographicsEndpoint, Demographic);
  public putDemographic = this.apiUtils.createPutFunction(demographicsEndpoint, Demographic);
  public deleteDemographic = this.apiUtils.createDeleteFunction(demographicsEndpoint);

  //
  // Estimated delivery statistics
  //
  public getEstimatedDeliveryStatisticsScreenAndSiteCount = this.apiUtils.createApiFunction(
    deliveryStatisticsScreenAndSiteCount,
    'POST',
    DeliveryStatistics,
    false,
  );

  public getEstimatedDeliveryStatistics = this.apiUtils.createApiFunction(
    deliveryStatisticsEndpoint,
    'POST',
    DeliveryStatistics,
    false,
    null,
    (url, params, data) => {
      const { isPrivate = true, planUuid = null } = data;
      if (!isPrivate && planUuid === null) {
        throw new Error('getEstimatedDeliveryStatistics needs a planUuid for a public plan');
      }
      let endPoint = isPrivate ? deliveryStatisticsEndpoint : `/plans/${planUuid}/deliverystatisticsestimate`;
      if (data.with_language) {
        endPoint = `${endPoint}?with_language=1`;
      }
      // This is used only in plan details.
      if (data.with_impressions_by_unit_id) {
        endPoint = `${endPoint}&with_impressions_by_unit_id=1`;
      }
      return endPoint;
    },
  );

  public getEstimatedReachStatistics = this.apiUtils.createApiFunction(estimatedReachEndpoint, 'POST', ReachStatistics);

  public getAudienceConcentration = this.apiUtils.createApiFunction(
    audienceConcentration,
    'POST',
    AudienceConcentrationGroupings,
  );

  public getCuratedPackageEstimatedDeliveryStatistics = this.apiUtils.createApiFunction(
    deliveryStatisticsEndpoint,
    'POST',
    CuratedPackageDeliveryStatistics,
    false,
    null,
    (url, params, data) => {
      let endPoint = curatedPackagesEstimatedDeliveryStatsEndpoint;
      if (data.with_language) {
        endPoint = `${endPoint}?with_language=1`;
      }
      // This is used only in plan details.
      if (data.with_impressions_by_unit_id) {
        endPoint = `${endPoint}&with_impressions_by_unit_id=1`;
      }
      return endPoint;
    },
  );

  public getCurateUnitCo2eEmissionsByVenueType = this.apiUtils.createApiFunction(
    `${curateUnitCo2eEmissionsEndpoint}averages/venue_types`,
    'POST',
    CurateUnitCo2eEmissionsByVenueType,
    true,
  );

  public getTargetedUnitAndSiteIDs = this.apiUtils.createApiFunction(
    deliveryStatisticsTargetedIDsEndpoint,
    'POST',
    null,
    false,
    null,
    null,
    false,
    (data) => {
      return {
        unitIDs: data.unit_ids,

        siteIDs: data.site_ids,
      };
    },
  );

  /**
   * @returns {Promise<{ unit_count: number }>}
   */
  public getUnitsMissingAudienceDataCount = this.apiUtils.createApiFunction(
    deliveryStatisticsUnitsMissingAudienceDataCountEndpoint,
    'POST',
    null,
  );

  /**
   * @returns {Promise<UnitInfo[]>}
   */
  public getUnitsMissingAudienceDataBrief = this.apiUtils.createApiFunction(
    deliveryStatisticsUnitsMissingAudienceDataBriefEndpoint,
    'POST',
  );

  //
  // External events
  //
  public getCustomEvents = this.apiUtils.createGetAllFunction(customEventsEndpoint, CustomEvent);
  public getCustomEvent = this.apiUtils.createGetOneFunction(customEventsEndpoint, CustomEvent);
  public postCustomEvents = this.apiUtils.createPostFunction(customEventsEndpoint, CustomEvent, null);
  public putCustomEvent = this.apiUtils.createPutFunction(customEventsEndpoint, CustomEvent, null);
  public deleteCustomEvent = this.apiUtils.createDeleteFunction(customEventsEndpoint);
  public getCustomEventsActivationStatus = this.apiUtils.createApiFunction(`${customEventsEndpoint}status`);
  public activateCustomEvents = this.apiUtils.createApiFunction(`${customEventsEndpoint}status/enable`, 'post');

  //
  // Journey analyses
  //
  public getJourneyAnalyses = this.apiUtils.createGetAllFunction(
    `${journeyAnalysesEndpoint}fororganizationid`,
    JourneyAnalysis,
  );
  public getJourneyAnalysis = this.apiUtils.createApiFunction(
    `${journeyAnalysesEndpoint}forworkflowconfigurationid/`,
    'POST',
    JourneyAnalysis,
    false,
    () => new QueryParams(),
    (u: string, params?: QueryParams) => `${u}${params?.customQueryParams?.id}`,
  );
  public postJourneyAnalysis = this.apiUtils.createPostFunction<JourneyAnalysis, JourneyPostResponse>(
    `${journeyAnalysesEndpoint}workflowconfiguration/create`,
    JourneyPostResponse,
  );
  public putJourneyAnalysis = this.apiUtils.createPutFunction(
    `${journeyAnalysesEndpoint}forworkflowconfigurationid/`,
    JourneyAnalysis,
  );
  public deleteJourneyAnalyses = this.apiUtils.createDeleteFunction(
    `${journeyAnalysesEndpoint}forworkflowconfigurationid/`,
  );
  public getJourneyAnalysisUnits = this.apiUtils.createGetAllFunction(
    `${journeyAnalysesEndpoint}forworkflowconfigurationid/{id}/units`,
    null,
    null,
    null,
    null,
    (u: string, params?: QueryParams) => u.replace('{id}', params?.customQueryParams?.id),
  );

  //
  // Journey analyses (OLD)
  //
  public getOldJourneyAnalyses = this.createGetAllFunctionLocal(localJourneyAnalysesEndpoint, OldJourneyAnalysis);
  public getOldJourneyAnalysis = this.createGetOneFunctionLocal(localJourneyAnalysesEndpoint, OldJourneyAnalysis);
  public postOldJourneyAnalysis = this.createPostFunctionLocal(localJourneyAnalysesEndpoint, OldJourneyAnalysis);
  public putOldJourneyAnalysis = this.createPutFunctionLocal(localJourneyAnalysesEndpoint, OldJourneyAnalysis);
  public deleteOldJourneyAnalysis = this.createDeleteFunctionLocal(localJourneyAnalysesEndpoint, OldJourneyAnalysis);

  // Lift dashboard
  public getLiftDashboards = this.apiUtils.createGetAllFunction(liftDashboardsEndpoint, LiftDashboardApiModel);
  public getLiftDashboard = this.apiUtils.createGetOneFunction(liftDashboardsEndpoint, LiftDashboardApiModel);
  public postLiftDashboards = this.apiUtils.createPostFunction(liftDashboardsEndpoint, LiftDashboardApiModel, null);
  public putLiftDashboard = this.apiUtils.createPutFunction(liftDashboardsEndpoint, LiftDashboardApiModel, null);
  public deleteLiftDashboard = this.apiUtils.createDeleteFunction(liftDashboardsEndpoint);
  public getPublicLiftDashboard = this.apiUtils.createApiFunction(
    `${liftDashboardsEndpoint}uuid/`,
    'get',
    LiftDashboardApiModel,
    true,
  );

  // https://apps.hivestack.com/api/ui/org/liftanalyses
  // ?$expand=results_overall,results_per_target.geofence,results_per_day,results_per_unit.unit.geofence
  // &$filter=lift_analysis_lineitem_links.lineitem.hivestack_id in (238)
  public getLiftAnalysisData = async ({ queryParams }) => {
    queryParams.expand = [
      ...(queryParams.expand || []),
      'results_overall',
      'results_per_creative',
      'results_per_market',
      'results_per_location',
      'results_per_target.geofence',
      'results_per_day',
      'results_per_unit.unit',
    ];
    queryParams.include = [
      ...(queryParams.include || []),
      'id',
      'name',
      'results_overall',
      'results_per_creative',
      'results_per_market',
      'results_per_location',
      'results_per_target.geofence',
      'results_per_day',
      'workflowexecution_id',
      'created_on_utc',
      'results_per_target.control_group_total',
      'results_per_target.control_group_total_pre_campaign',
      'results_per_target.control_group_visited',
      'results_per_unit.control_group_total',
      'results_per_unit.control_group_total_pre_campaign',
      'results_per_unit.control_group_visited',
      'results_per_target.control_group_visited_pre_campaign',
      'results_per_target.exposed_to_ad_group_total',
      'results_per_target.exposed_to_ad_group_total_pre_campaign',
      'results_per_unit.control_group_visited_pre_campaign',
      'results_per_unit.exposed_to_ad_group_total',
      'results_per_unit.exposed_to_ad_group_total_pre_campaign',
      'results_per_target.exposed_to_ad_group_visited',
      'results_per_target.exposed_to_ad_group_visited_pre_campaign',
      'results_per_unit.exposed_to_ad_group_visited',
      'results_per_unit.exposed_to_ad_group_visited_pre_campaign',
      'results_per_unit.unit.name',
      'results_per_unit.unit.media_type',
      'results_per_unit.unit.latitude',
      'results_per_unit.unit.longitude',
      'results_per_unit.unit.hivestack_organization_id',
      'results_per_unit.unit.hivestack_id',
      'results_per_target.id',
      'results_per_unit.id',
    ];
    const { data } = await this.axiosInstance.get(liftAnalysesEndpoint, { params: queryParams.toObject() });
    // This filters out any failed lift analyses
    const response = data.filter((r) => r.results_overall);
    return response.map((r) => LiftAnalysisData.fromApi(r));
  };
  public getLiftAnalysisSummary = async ({ queryParams }) => {
    queryParams.expand = [...(queryParams.expand || []), 'results_overall'];
    const { data } = await this.axiosInstance.get(liftAnalysesEndpoint, { params: queryParams.toObject() });
    // This filters out any failed lift analyses
    const response = data.filter((r) => r.results_overall);
    return response.map((r) => LiftAnalysisSummary.fromApi(r));
  };

  //
  // Line items
  //
  public putLineItem = this.apiUtils.createPutFunction(lineItemsEndpoint, LineItem, null);
  public patchLineItems = this.apiUtils.createPatchFunction(lineItemsEndpoint);
  public getLineItemDemographicTargets = this.apiUtils.createApiFunction(
    lineItemDemographicTargetsEndpoint,
    'get',
    LineItemDemographic,
    true,
  );
  public getLineItemCustomFields = this.apiUtils.createApiFunction(`${lineItemsEndpoint}customfields/`, 'get');

  //
  // Line item media Owner Approvals
  //
  public getLineItemMediaOwnerApprovals = this.apiUtils.createGetAllFunction(
    lineItemMediaOwnerApprovalsEndpoint,
    LineItemMediaApproval,
  );

  //
  // Mobile line items
  //
  public getMobileLineItems = this.apiUtils.createGetAllFunction(
    mobileLineItemsEndpoint,
    MobileLineItem,
    mobileLineItemParamsTransform,
  );
  public getMobileLineItem = this.apiUtils.createGetOneFunction(
    mobileLineItemsEndpoint,
    MobileLineItem,
    mobileLineItemParamsTransform,
  );
  public postMobileLineItems = this.apiUtils.createPostFunction(
    mobileLineItemsEndpoint,
    MobileLineItem,
    null,
    mobileLineItemParamsTransform,
  );
  public putMobileLineItem = this.apiUtils.createPutFunction(
    mobileLineItemsEndpoint,
    MobileLineItem,
    null,
    mobileLineItemParamsTransform,
  );
  public deleteMobileLineItem = this.apiUtils.createDeleteFunction(mobileLineItemsEndpoint);
  public getMobileLineItemAudit = this.apiUtils.createGetAuditFunction(mobileLineItemsEndpoint, 'MobileLineItem');

  //
  // Networks
  //
  public getNetworks = this.apiUtils.createGetAllFunction(networksEndpoint, Network);
  public getNetwork = this.apiUtils.createGetOneFunction(networksEndpoint, Network);
  public postNetwork = this.apiUtils.createPostFunction(networksEndpoint, Network, null);
  public putNetwork = this.apiUtils.createPutFunction(networksEndpoint, Network, null);

  //
  // Organization custom fields
  //
  public getOrganizationCustomFields = this.apiUtils.createGetAllFunction(
    organizationCustomFieldsEndpoint,
    OrganizationCustomField,
    // Exclude custom fields associated with plan entities (deprecated in CAM-5483
    // while preserving existing query params
    (qp) => {
      if (qp.filterCollection?.filters?.length > 0) {
        qp.filterCollection.filters = [...(qp.filterCollection.filters as Filter[])];
        if (qp.filterCollection.filters.find((x) => x.property !== 'entity' && x.values && x.values[0] !== 'plan')) {
          qp.filterCollection.filters.push(new Filter('entity', ['plan'], FilterComparisonOperator.NOT_EQUAL));
        }
      } else {
        qp.filterCollection.filters = [new Filter('entity', ['plan'], FilterComparisonOperator.NOT_EQUAL)];
      }

      qp.expand = [...(qp.expand || []), 'organizationcustomvalues'];

      return qp;
    },
  );
  public getOrganizationCustomField = this.apiUtils.createGetOneFunction(
    organizationCustomFieldsEndpoint,
    OrganizationCustomField,
    (qp) => {
      qp.expand = [...(qp.expand || []), 'organizationcustomvalues'];
      return qp;
    },
  );
  public postOrganizationCustomFields = this.apiUtils.createPostFunction(
    organizationCustomFieldsEndpoint,
    OrganizationCustomField,
    null,
  );
  public putOrganizationCustomField = this.apiUtils.createPutFunction(
    organizationCustomFieldsEndpoint,
    OrganizationCustomField,
    null,
  );
  public deleteOrganizationCustomField = this.apiUtils.createDeleteFunction(organizationCustomFieldsEndpoint);
  public postOrganizationCustomFieldsValue = this.apiUtils.createPostFunction(
    organizationCustomFieldsValuesEndpoint,
    OrganizationCustomFieldValue,
  );
  public updateOrganizationCustomFieldsValue = this.apiUtils.createPutFunction(
    organizationCustomFieldsValuesEndpoint,
    OrganizationCustomFieldValue,
    null,
  );
  public deleteOrganizationCustomFieldValue = this.apiUtils.createDeleteFunction(
    organizationCustomFieldsValuesEndpoint,
  );

  //
  // Owner Yield Manager SSPs
  //
  public getOwnerYieldManagerSsps = this.apiUtils.createGetAllFunction(
    ownerYieldManagerSspsEndpoint,
    OwnerYieldManagerSsp,
  );
  public getOwnerYieldManagerSsp = this.apiUtils.createGetOneFunction(
    ownerYieldManagerSspsEndpoint,
    OwnerYieldManagerSsp,
  );
  public postOwnerYieldManagerSsp = this.apiUtils.createPostFunction(
    ownerYieldManagerSspsEndpoint,
    OwnerYieldManagerSsp,
    null,
  );
  public putOwnerYieldManagerSsp = this.apiUtils.createPutFunction(
    ownerYieldManagerSspsEndpoint,
    OwnerYieldManagerSsp,
    null,
  );
  public deleteOwnerYieldManagerSsp = this.apiUtils.createDeleteFunction(ownerYieldManagerSspsEndpoint);
  public createSspWithDomain = this.apiUtils.createApiFunction(
    `${ownerYieldManagerSspsEndpoint}createwithdomain`,
    'post',
    OwnerYieldManagerSsp,
    false,
  );
  public getYieldManagerBidStats = this.apiUtils.createApiFunction(
    `${yieldManagerEndpoint}bids/statistics`,
    'get',
    YieldManagerBidStats,
    false,
    (params: any) => ({
      from_datetime: toServerDateTimeNoTimezone(params.startDate),
      to_datetime: toServerDateTimeNoTimezone(params.endDate),
    }),
  );

  public uploadOwnerYieldManagerSspLogo = async (file, id, cancelTokenSource) => {
    const headers = {
      'Content-Type': file.type,
    };
    const response = await this.axiosInstance
      .request({
        method: 'POST',
        url: `${ownerYieldManagerSspsEndpoint}${id}/logo/upload`,
        data: file,
        headers,
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response;
  };

  //
  // Yield Manager - Complex Auctions Configs
  //
  public getComplexAuctionsConfigs = this.apiUtils.createApiFunction(
    `${yieldManagerEndpoint}complexauctionsconfigs/`,
    'get',
    HeaderBidderSettings,
    true,
  );

  public createComplexAuctionsConfigs = this.apiUtils.createPostFunction(
    `${yieldManagerEndpoint}complexauctionsconfigs/`,
    HeaderBidderSettings,
  );

  public updateComplexAuctionsConfigs = this.apiUtils.createPutFunction(
    `${yieldManagerEndpoint}complexauctionsconfigs/`,
    HeaderBidderSettings,
  );

  // Packages
  public getPackageCustomFields = this.apiUtils.createApiFunction(`${packagesEndpoint}customfields/`, 'get');
  public getCuratorPackage = this.apiUtils.createGetOneFunction(`${curatorSuppliersEndpoint}packages/`, Package);
  public getCuratorPackages = this.apiUtils.createGetAllFunction(`${curatorSuppliersEndpoint}packages/`, Package);

  //
  // Plans
  //
  public getPlans = this.apiUtils.createGetAllFunction(plansEndpoint, Plan);
  public getPlan = this.apiUtils.createGetOneFunction(plansEndpoint, Plan);
  public postPlan = this.apiUtils.createPostFunction(plansEndpoint, Plan, null);
  public putPlan = this.apiUtils.createPutFunction(plansEndpoint, Plan, null);
  public deletePlan = this.apiUtils.createDeleteFunction(plansEndpoint);
  public sharePlan = this.apiUtils.createPutFunction(plansEndpoint, SharedPublicEntity);
  public getPublicSharedPlan = this.apiUtils.createPostFunction(`${plansEndpoint}shared`, SharedPlan as any);
  public getPlanBreakdownData = this.apiUtils.createApiFunction(
    plansBreakdownDataEndpoint,
    'post',
    PlanBreakdownData as any,
  );

  public getPlanApprovals = this.apiUtils.createApiFunction(plansEndpoint, 'get', PlanApproval, true);
  public postPlanApprovals = this.apiUtils.createPostFunction(`${planApprovalsEndpoint}submit`, PlanApproval);
  public getPublicPlanGeofenceGroups = this.apiUtils.createGetAllFunction(
    `${plansEndpoint}{planUuid}/geofencegroups`,
    GeofenceGroup,
    null,
    null,
    null,
    (url, params) => url.replace('{planUuid}', params.customQueryParams.uuid),
  );
  public extendPublicPlan = this.apiUtils.createApiFunction(
    'plans/{planID}/extendsharedexpiry',
    'post',
    null,
    false,
    null,
    (url, _, data) => url.replace('{planID}', data.id),
  );

  //
  // Audience files
  //
  public getPostalCodesFiles = this.apiUtils.createGetAllFunction(postalCodesFilesEndpoint, PostalCodesFile);
  public getPostalCodesFile = this.apiUtils.createGetOneFunction(postalCodesFilesEndpoint, PostalCodesFile);
  // public putPostalCodesFile = this.apiUtils.createPutFunction(postalCodesFilesEndpoint, PostalCodesFile, null);
  // public deletePostalCodesFile = this.apiUtils.createDeleteFunction(postalCodesFilesEndpoint);

  //
  // Audience files
  //
  public getAudienceFiles = this.apiUtils.createGetAllFunction(audienceFilesEndpoint, AudienceFile);
  public getAudienceFile = this.apiUtils.createGetOneFunction(audienceFilesEndpoint, AudienceFile);
  public putAudienceFile = this.apiUtils.createPutFunction(audienceFilesEndpoint, AudienceFile, null);
  public deleteAudienceFile = this.apiUtils.createDeleteFunction(audienceFilesEndpoint);

  //
  // Report definitions
  //
  public getReportDefinitions = this.apiUtils.createGetAllFunction(
    reportDefinitionsEndpoint,
    ReportDefinition,
    reportDefinitionParamsTransform,
  );
  public getReportDefinition = this.apiUtils.createGetOneFunction(
    reportDefinitionsEndpoint,
    ReportDefinition,
    reportDefinitionParamsTransform,
  );
  public postReportDefinitions = this.apiUtils.createPostFunction(
    reportDefinitionsEndpoint,
    ReportDefinition,
    null,
    reportDefinitionParamsTransform,
  );
  public putReportDefinition = this.apiUtils.createPutFunction(
    reportDefinitionsEndpoint,
    ReportDefinition,
    null,
    reportDefinitionParamsTransform,
  );
  public deleteReportDefinition = this.apiUtils.createDeleteFunction(reportDefinitionsEndpoint);
  public getReportDefinitionAudit = this.apiUtils.createGetAuditFunction(reportDefinitionsEndpoint, 'ReportDefinition');

  //
  // Report executions
  //
  public getReportExecutions = this.apiUtils.createGetAllFunction(reportExecutionsEndpoint, ReportExecution);
  public getReportExecution = this.apiUtils.createGetOneFunction(
    reportExecutionsEndpoint,
    ReportExecution,
    uniqueReportExecutionParamsTransform,
  );
  public deleteReportExecution = this.apiUtils.createDeleteFunction(reportExecutionsEndpoint);
  public getFutureReportExecutions = this.apiUtils.createApiFunction('reportrunevents/previewfutureevents', 'post');
  public getAdvancedReportExecutions = this.apiUtils.createGetAllFunction(
    advancedReportExecutionsEndpoint,
    AdvancedReportExecution,
  );

  // getTargetedUnitAndSiteIDs(params, successCallback, errorCallback) {
  //   public axiosInstance
  //     .request({
  //       url: deliveryStatisticsTargetedIDsEndpoint,
  //       method: 'POST',
  //       data: params,
  //       cancelToken: params.cancelTokenSource != null ? params.cancelTokenSource.token : null,
  //     })
  //     .then(
  //       (successResponse) => {
  //         if (successResponse == null) return;
  //         // eslint-disable-next-line camelcase
  //         const { unit_ids, site_ids } = successResponse.data;
  //         successCallback({
  //           // eslint-disable-next-line camelcase
  //           unitIDs: unit_ids,
  //           // eslint-disable-next-line camelcase
  //           siteIDs: site_ids,
  //         });
  //       },
  //       (errorResponse) => {
  //         errorCallback(getErrorOld(errorResponse), getStatusCode(errorResponse));
  //         // eslint-disable-next-line no-console
  //         console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
  //       },
  //     );
  // }
  //
  //
  // Report schedules
  //
  public getReportSchedules = this.apiUtils.createGetAllFunction(
    reportSchedulesEndpoint,
    ReportSchedule,
    reportScheduleParamsTransform,
  );
  public getReportSchedule = this.apiUtils.createGetOneFunction(
    reportSchedulesEndpoint,
    ReportSchedule,
    reportScheduleParamsTransform,
  );
  public postReportSchedules = this.apiUtils.createPostFunction(
    reportSchedulesEndpoint,
    ReportSchedule,
    null,
    reportScheduleParamsTransform,
  );
  public putReportSchedule = this.apiUtils.createPutFunction(
    reportSchedulesEndpoint,
    ReportSchedule,
    null,
    reportScheduleParamsTransform,
  );
  public deleteReportSchedule = this.apiUtils.createDeleteFunction(reportSchedulesEndpoint);
  public getReportScheduleAudit = this.apiUtils.createGetAuditFunction(reportSchedulesEndpoint, 'ReportSchedule');

  //
  // Reference data
  //
  public getUniqueAdvertisers = this.apiUtils.createGetAllFunction(uniqueAdvertiserEndpoint, UniqueAdvertiser);
  public getAvailableUniqueAdvertisers = this.apiUtils.createGetAllFunction(
    uniqueAdvertiserEndpoint,
    UniqueAdvertiser,
    uniqueAdvertiserParamsTransform,
    [new Filter('dirty', [false]), new Filter('replacement_id', [null])],
  );

  public getLanguages = this.apiUtils.createGetAllFunction(languagesEndpoint, Language);
  public getBuyersAsync = this.apiUtils.createGetAllFunction(buyersEndpoint, Organization);
  public getCurators = this.apiUtils.createGetAllFunction(curatorsEndpoint, Organization);
  public getTags = this.apiUtils.createApiFunction(tagsEndpoint, 'get');
  public getMediaTypes = this.apiUtils.createGetAllFunction(mediaTypesEndpoint, MediaType);
  public getGeofenceGroups = this.apiUtils.createGetAllFunction(geofenceGroupsEndpoint, GeofenceGroup);
  public getGeofences = this.apiUtils.createGetAllFunction(geofencesEndpoint, Geofence);
  public getLocationsByLevel = this.apiUtils.createApiFunction(`${locationsEndpoint}getlocationsbylevel`, 'get');
  public getLocationsAsync = this.apiUtils.createGetAllFunction(locationsEndpoint, Location);
  public getMarkets = this.apiUtils.createGetAllFunction(marketsEndpoint, Market);
  public getMarketsWithInventory = this.apiUtils.createApiFunction(
    `${marketsEndpoint}withinventory`,
    'get',
    Market,
    true,
  );
  public getSupplierCountryRoots = this.apiUtils.createApiFunction(`${locationsEndpoint}roots`, 'get');
  public getFilterChanges = this.apiUtils.createApiFunction('unitfilters/filtervalues', 'post');
  public getOrganizationDSPBuyers = this.apiUtils.createApiFunction(
    `${organizationDSPsEndpoint}buyers`,
    'post',
    null,
    false,
  );

  //
  // Revenue split
  //
  public postMediaOwnerRevenueSplit = this.apiUtils.createApiFunction(
    `${revenueSplit}mediaowner`,
    'POST',
    RevenueSplit,
    false,
  );

  //
  // Units
  //
  public getUnitsInGeofences = this.apiUtils.createApiFunction(`${unitsEndpoint}ingeofences`, 'post'); // Not used anywhere
  public getUnitIDsInGeofences = this.apiUtils.createApiFunction(`${unitsEndpoint}ingeofences/ids`, 'post');
  public patchUnits = this.apiUtils.createPatchFunction(unitsEndpoint);
  public getUnits = (params, successCallback, errorCallback) => {
    const { cancelTokenSource, ...apiParams } = params;
    const data = ApiUtils.getBodyData(apiParams.queryParams);
    this.axiosInstance
      .request({
        url: `${unitsEndpoint}postgetlist`,
        method: 'POST',
        data,
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) return;
          successCallback(ApiUtils.getResult(successResponse, Unit));
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          if (!(errorResponse instanceof CanceledError)) {
            console.error(
              errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error',
            );
          }
        },
      );
  };

  //
  // Sites
  //
  public patchSites = this.apiUtils.createPatchFunction(sitesEndpoint);
  public getSites = (params, successCallback, errorCallback) => {
    const { cancelTokenSource, ...apiParams } = params;
    const data = ApiUtils.getBodyData(apiParams.queryParams);
    this.axiosInstance
      .request({
        url: `${sitesEndpoint}postgetlist`,
        method: 'POST',
        data,
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) return;
          successCallback(ApiUtils.getResult(successResponse, Site));
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          if (!(errorResponse instanceof CanceledError)) {
            console.error(
              errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error',
            );
          }
        },
      );
  };

  //
  // Unit packs
  //
  public getUnitPacks = this.apiUtils.createGetAllFunction(unitPacksEndpoint, UnitPack);
  public getUnitPack = this.apiUtils.createGetOneFunction(unitPacksEndpoint, UnitPack);
  public postUnitPack = this.apiUtils.createPostFunction(unitPacksEndpoint, UnitPack, null);
  public putUnitPack = this.apiUtils.createPutFunction(unitPacksEndpoint, UnitPack, null);
  public deleteUnitPack = this.apiUtils.createDeleteFunction(unitPacksEndpoint);
  public patchUnitPacks = this.apiUtils.createPatchFunction(unitPacksEndpoint);

  //
  // Unit Audience Concentration
  //
  public getDailyAndAverageConcentrationPerUnit = this.apiUtils.createApiFunction(
    dailyAndAverageConcentrationPerUnitEndpoint,
    'post',
    AudienceConcentrationPerUnit,
    false,
    null,
    null,
    true,
  );

  public getUnitsForPublicPlan = async ({ params, cancelTokenSource, planUuid }) => {
    try {
      const url = `/plans/${planUuid}/units`;
      const res = await this.axiosInstance.get(url, {
        params,
        cancelToken: cancelTokenSource.token,
      });
      if (!res?.data?.length) return null;
      return { items: res.data.map((unit) => Unit.fromApi(unit)) };
    } catch (err) {}
  };

  public getSitesForPublicPlan = async ({ params, cancelTokenSource, planUuid }) => {
    try {
      const url = `/plans/${planUuid}/sites`;
      const res = await this.axiosInstance.get(url, {
        params,
        cancelToken: cancelTokenSource.token,
      });
      if (!res?.data?.length) return null;
      return { items: res.data.map((site) => Site.fromApi(site)) };
    } catch (err) {
      throw ApiUtils.getError(err);
    }
  };

  public getNetworksForPublicPlan = async ({ password, planUuid }) => {
    try {
      const url = `/plans/${planUuid}/networks`;
      const res = await this.axiosInstance.post(url, password);
      if (!res?.data?.length) return null;
      return { items: res.data.map((network) => Network.fromApi(network)) };
    } catch (err) {
      throw ApiUtils.getError(err);
    }
  };

  //
  // Unit pack inventory targeting
  //
  public getUnitPackInventoryTargeting = (params, successCallback, errorCallback) => {
    this.axiosInstance
      .request({
        url: `${unitPacksEndpoint}${params.id}/inventorytargeting`,
        method: 'GET',
        cancelToken: params.cancelTokenSource != null ? params.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) return;
          successCallback(UnitPackInventoryTargeting.fromApi(successResponse.data));
        },
        (errorResponse) => {
          errorCallback(getErrorOld(errorResponse), getStatusCode(errorResponse));

          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  };
  public createUnitPackInventoryTargeting = (params, successCallback, errorCallback) => {
    this.axiosInstance
      .request({
        url: `${unitPacksEndpoint}${params.id}/inventorytargeting`,
        method: 'POST',
        data: params.data,
        cancelToken: params.cancelTokenSource != null ? params.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) return;
          successCallback(UnitPackInventoryTargeting.fromApi(successResponse.data));
        },
        (errorResponse) => {
          errorCallback(getErrorOld(errorResponse), getStatusCode(errorResponse));

          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  };
  public updateUnitPackInventoryTargeting = (params, successCallback, errorCallback) => {
    this.axiosInstance
      .request({
        url: `${unitPacksEndpoint}${params.id}/inventorytargeting`,
        method: 'PUT',
        data: params.data,
        cancelToken: params.cancelTokenSource != null ? params.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) return;
          successCallback(UnitPackInventoryTargeting.fromApi(successResponse.data));
        },
        (errorResponse) => {
          errorCallback(getErrorOld(errorResponse), getStatusCode(errorResponse));

          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  };
  public unitPackUpdateUnits = (params, successCallback, errorCallback) => {
    this.axiosInstance
      .request({
        url: `${unitPacksEndpoint}${params.id}/updateunits`,
        method: 'POST',
        cancelToken: params.cancelTokenSource != null ? params.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) return;
          successCallback(UnitPackInventoryTargeting.fromApi(successResponse.data));
        },
        (errorResponse) => {
          errorCallback(getErrorOld(errorResponse), getStatusCode(errorResponse));

          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  };

  //
  // TTD
  //
  public postTTDPlan = this.apiUtils.createApiFunction(
    'plans/{id}/pushtottd/',
    'post',
    null,
    false,
    null,
    (url, params) => url.replace('{id}', params.id),
  );

  //
  // Users
  //
  public getUserOrganizations = this.apiUtils.createGetAllFunction(
    userOrganizationsEndpoint,
    UserOrganization,
    userOrganizationParamsTransform,
  );
  public putUserOrganization = this.apiUtils.createPutFunction(
    userOrganizationsEndpoint,
    UserOrganization,
    null,
    userOrganizationParamsTransform,
  );
  public deleteUserOrganization = this.apiUtils.createDeleteFunction(userOrganizationsEndpoint);
  public getUserOrganization = this.apiUtils.createGetAllFunction(
    userOrganizationsEndpoint,
    UserOrganization,
    userOrganizationParamsTransform,
  );

  public uploadUserProfilePicture = async (file, id, cancelTokenSource) => {
    const headers = {
      'Content-Type': file.type,
      user_id: id, // This is unusual but the endpoint expects the user ID in the headers...
    };
    const response = await this.axiosInstance
      .request({
        method: 'POST',
        url: `${usersEndpoint}profilepicture`,
        data: file,
        headers,
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response;
  };

  public deleteUserProfilePicture = async (id, cancelTokenSource) => {
    const headers = {
      user_id: id, // This is unusual but the endpoint expects the user ID in the headers...
    };
    const response = await this.axiosInstance
      .request({
        method: 'DELETE',
        url: `${usersEndpoint}profilepicture`,
        headers,
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response;
  };

  //
  // UserSettings
  //
  public getUserSettings = this.apiUtils.createGetAllFunction(userSettingsEndpoint, UserSetting);
  public postUserSetting = this.apiUtils.createPostFunction<UserSetting>(userSettingsEndpoint, UserSetting, null);
  public deleteUserSetting = this.apiUtils.createDeleteFunction(userSettingsEndpoint);

  // Package Group
  private createMethodPackageGroup = (method: 'get' | 'post' | 'put', params) => {
    const endpoint = rootState?.orgApp?.organization?.isSidekickOrganization
      ? externalPackageGroupsEndpoint
      : packageGroupsEndpoint;
    let newMethod = null;
    switch (method) {
      case 'get':
        newMethod = this.apiUtils.createGetOneFunction(endpoint, PackageGroup);
        break;
      case 'post':
        newMethod = this.apiUtils.createPostFunction(endpoint, PackageGroup);
        break;
      case 'put':
        newMethod = this.apiUtils.createPutFunction(endpoint, PackageGroup);
        break;
      default:
        break;
    }
    return newMethod({ ...params });
  };
  public getPackageGroup = (params) => this.createMethodPackageGroup('get', params);
  public postPackageGroup = (params) => this.createMethodPackageGroup('post', params);
  public putPackageGroup = (params) => this.createMethodPackageGroup('put', params);

  static get instance(): APIProxy {
    if (!this[apiInstance]) {
      this[apiInstance] = new APIProxy();
    }
    return this[apiInstance];
  }

  static reset() {
    APIProxy.instance.axiosInstanceInternal = null;
  }

  get axiosInstance() {
    if (this.axiosInstanceInternal == null) {
      const org = LocalCache.get('organization');
      const orgID = org != null ? Organization.fromApi(org).id : null;
      const headers: Record<string, string | number> = {};

      if (LocalCache.get('token') != null) {
        headers.Authorization = `Bearer ${LocalCache.get('token')}`;
        const org = LocalCache.get('organization');
        // For curate currency conversion in Dashboard. Most UAT and Staging curate orgs are using playlogs data right now, but we need to use insights data for currency conversion.
        if (
          org != null &&
          org.type === OrganizationType.CURATOR &&
          (environment === Environment.DEV || environment === Environment.STAGING || environment === Environment.UAT)
        ) {
          headers['x-insights-backend'] = 'insights';
        }
      }
      if (orgID != null) {
        headers['ax-organization-id'] = orgID;
      }
      this.axiosInstanceInternal = axios.create({
        baseURL: BaseApiUrl,
        headers,
      });

      this.axiosInstanceInternal.interceptors.request.use((config) => {
        if (rootState.isAppOffline) {
          return {
            ...config,
            cancelToken: new axios.CancelToken((cancel) => cancel('Cancel repeated request')),
          };
        }
        return config;
      });
      this.axiosInstanceInternal.interceptors.response.use(
        (response) => {
          return response;
        },
        (error): Promise => {
          if (axios.isCancel(error)) {
            console.log('Request cancelled');
            return Promise.reject(error);
          }
          if (error.response.data != null && error.response.data.error === 'JWT signature is invalid') {
            GlobalEventBus.$emit(GlobalEvents.resetToken);
          } else if (error.response.request.responseURL.indexOf('/login') === -1) {
            if (error.response.data != null && error.response.data.error === 'User password is expired') {
              GlobalEventBus.$emit(GlobalEvents.logoutDueToPasswordExpiry);
            } else if (error.response.status === 401) {
              /**
               * Emit a logout messages unless the user is trying to change his pwd or accessing a public shared plan
               *
               * @note Public share plan reuses components from the private app
               * It may happen that some changes are made without thinking about public share plan (ie: calling the BE on mounted())
               * It'll throw a 401 causing the public share plan to be redirected to the Hivestack login
               * To prevent this, don't @$emit logout when the user is on a public share plan
               * We still throw the error (they may report to us they are seeing some error notification popping up so we can fix it)
               */
              if (
                !error.request.responseURL.includes('changepassword') &&
                !window.location.pathname.includes('proposal') &&
                !window.location.pathname.includes('monitoring') &&
                !window.location.pathname.includes('lift') &&
                !window.location.pathname.includes('heat') &&
                !error.config.url.includes('refreshtoken') &&
                LocalCache.get('refreshToken') != null
              ) {
                return this.attemptTokenRefresh(error.config);
              }
            }
          }

          return Promise.reject(error);
        },
      );
    }
    return this.axiosInstanceInternal;
  }

  attemptTokenRefresh(config) {
    const refreshToken = LocalCache.get('refreshToken');
    if (refreshToken != null) {
      // If we are not already refreshing the token, do so.
      if (!this.isRefreshingToken) {
        this.isRefreshingToken = true;
        this.refreshToken()
          .then(() => {
            this.isRefreshingToken = false;
            this.resumeRequestsExecution();
          })
          .catch(() => {
            GlobalEventBus.$emit(GlobalEvents.logout);
            return false;
          });
      }
      // If we are refreshing the token, wait for it to finish before retrying the request.
      return new Promise((resolve) => {
        this.subscribeTokenRefresh(() => {
          // replace the expired token and retry
          if (config) {
            config.headers['Authorization'] = `Bearer ${LocalCache.get('token')}`;
            const api = this.axiosInstance;
            resolve(api(config));
          } else {
            resolve(true);
          }
        });
      });
    } else {
      GlobalEventBus.$emit(GlobalEvents.logout);
      return false;
    }
  }

  subscribeTokenRefresh(cb) {
    this.refreshSubscribers.push(cb);
  }

  resumeRequestsExecution() {
    this.refreshSubscribers.forEach((cb) => {
      try {
        cb();
      } catch (e) {
        console.log(e);
      }
    });
    this.refreshSubscribers = [];
  }

  get isProduction() {
    return window.location.host.indexOf('staging') === -1;
  }

  //
  // Download targeted screens
  //
  async getDownloadTargetedScreens({ type, format, id, cancelTokenSource = null }) {
    const url = `${type}/${id}/exportscreens/?format=${format}`;
    const response = await this.axiosInstance
      .request({
        url,
        method: 'GET',
        responseType: 'blob',
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response;
  }

  //
  // Download targeted sites
  //
  async getDownloadTargetedSites({ type, format, id, cancelTokenSource = null }) {
    const url = `${type}/${id}/exportsites/?format=${format}`;
    const response = await this.axiosInstance
      .request({
        url,
        method: 'GET',
        responseType: 'blob',
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response;
  }

  //
  // Download plan as a CSV or XLSX file
  //
  async getPlanExport({ planId, format, timeZone, cancelTokenSource = null }) {
    const url = `/plans/${planId}/export/?timezone=${timeZone}&format=${format}`;
    try {
      const response = await this.axiosInstance.request({
        url,
        method: 'GET',
        responseType: 'blob',
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      });

      // Extract filename from the content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'downloaded_file.zip';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      return {
        data: response.data,
        filename: filename,
      };
    } catch (err) {
      throw ApiUtils.getError(err);
    }
  }

  //
  // Local storage functions
  //
  createGetAllFunctionLocal(url, model) {
    return async ({ queryParams = QueryParams.all(), cancelTokenSource = null } = {}) => {
      const json = LocalCache.get(`api-${window.encodeURI(url)}`);
      if (json != null && json !== '') {
        try {
          const data = JSON.parse(json).map(model.fromApi);
          return {
            pageSize: data.length,
            pageOffset: 0,
            totalCount: data.length,
            items: data,
          };
        } catch (e) {}
      }
      return {
        pageSize: 10,
        pageOffset: 0,
        totalCount: 0,
        items: [],
      };
    };
  }

  createGetOneFunctionLocal(url, model) {
    return async ({ id }) => {
      const json = LocalCache.get(`api-${window.encodeURI(url)}`);
      if (json == null) {
        return null;
      }
      const data = JSON.parse(json).map(model.fromApi);
      return data.find((x) => x.id === id);
    };
  }

  createPostFunctionLocal(url, model) {
    // Instances can either be one instance or an array of instances. It will return either an array or a single instance depending on the input.
    return async ({ instances }) => {
      const serializedObjects = LocalCache.get(`api-${window.encodeURI(url)}`);
      let maxID = 0;
      let data = [];
      if (serializedObjects != null && serializedObjects !== '') {
        try {
          data = JSON.parse(serializedObjects).map(model.fromApi);
          maxID = data.length === 0 ? 0 : Math.max(...data.filter((x) => x.id != null).map((x) => x.id));
        } catch (e) {}
      }
      instances.forEach((x) => {
        maxID += 1;
        x.id = maxID;
      });
      const dataStr = JSON.stringify([...data, ...instances].map((x) => x.toApi()));
      LocalCache.set(`api-${window.encodeURI(url)}`, dataStr);
      return instances;
    };
  }

  createPutFunctionLocal(url, model) {
    // Instances can either be one instance or an array of instances. It will return either an array or a single instance depending on the input.
    return async ({ instance }) => {
      let data = JSON.parse(LocalCache.get(`api-${window.encodeURI(url)}`)).map(model.fromApi);
      data = JSON.stringify([...data.filter((x) => instance.id !== x.id), instance].map((x) => x.toApi()));
      LocalCache.set(`api-${window.encodeURI(url)}`, data);
      return instance;
    };
  }

  createDeleteFunctionLocal(url, model) {
    return async ({ instance }) => {
      const cache = JSON.parse(LocalCache.get(`api-${window.encodeURI(url)}`));
      let data = cache.map(model.fromApi);
      data = data.filter((x) => instance.id !== x.id).map((x) => x.toApi());
      LocalCache.set(`api-${window.encodeURI(url)}`, JSON.stringify(data));
    };
  }

  //
  // Authorization
  //
  login(username, password) {
    return new Promise<void>((resolve, reject) => {
      const payload = {
        email: username,
        password,
      };
      this.axiosInstance.post(loginEndpoint, payload).then(
        (successResponse) => {
          // Reset the axios instance to update the authorization header on next call.
          this.axiosInstanceInternal = null;
          if (successResponse !== undefined) {
            resolve(successResponse.data);
          } else {
            resolve();
          }
        },
        (errorResponse) => {
          reject([getErrorOld(errorResponse), getStatusCode(errorResponse)]);
        },
      );
    });
  }

  async refreshToken() {
    try {
      const payload = {
        refresh_token: LocalCache.get('refreshToken'),
      };
      const response = await this.axiosInstance.post(refreshTokenEndpoint, payload);
      LocalCache.set('token', response.data.token);
      LocalCache.set('refreshToken', response.data.refresh_token);
      this.axiosInstanceInternal = null;
      // this.axiosInstance.headers.Authorization = `Bearer ${LocalCache.get('token')}`;
    } catch (e) {
      LocalCache.remove('token');
      LocalCache.remove('refreshToken');
      GlobalEventBus.$emit(GlobalEvents.logout);
    }
  }

  sendResetPasswordEmail(email) {
    const payload = {
      email,
    };

    return this.axiosInstance.post(passwordResetEmailEndpoint, payload).catch((err) => {
      throw getErrorOld(err);
    });
  }

  resetPassword(token, password) {
    const payload = {
      token,
      password,
    };
    return this.axiosInstance.post(resetPasswordEndpoint, payload).catch((err) => {
      throw getErrorOld(err);
    });
  }

  sendVerificationEmail(userOrganizationID) {
    return this.axiosInstance.post(`${sendVerificationEmailEndpoint}${userOrganizationID}`).catch((err) => {
      throw getErrorOld(err);
    });
  }

  verifyEmail(token, password) {
    const payload = {
      token,
      password,
    };
    return this.axiosInstance.post(verifyEmailEndpoint, payload).catch((err) => {
      throw getErrorOld(err);
    });
  }

  getTheme() {
    return this.axiosInstance.get(themingEndpoint).catch((err) => {
      throw getErrorOld(err);
    });
  }

  async getPermissions() {
    const res = await this.axiosInstance.get(permissionsEndpoint).catch((err) => {
      throw ApiUtils.getError(err);
    });
    return Permissions.fromApi(res.data);
  }

  async getUser() {
    const res = await this.axiosInstance.get(currentUserEndpoint).catch((err) => {
      throw ApiUtils.getError(err);
    });
    return User.fromApi(res.data);
  }

  async getFreshdeskToken() {
    const res = await this.axiosInstance.get(freshdeskTokenEndpoint).catch((err) => {
      throw ApiUtils.getError(err);
    });
    return res.data.access_token;
  }

  getMembership(successCallback, errorCallback) {
    const subdomain = LocalCache.get('subdomain') || '';
    this.axiosInstance.get(`${membershipsEndpoint}?subdomain=${subdomain}`).then(
      (successResponse) => {
        successCallback(Membership.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // Advertisers
  //
  getAdvertiser(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${advertisersEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(Advertiser.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAdvertisers(successCallback, errorCallback) {
    this.axiosInstance
      .get(
        `${advertisersEndpoint}?$expand=unique_advertiser.advertiser_domains,unique_advertiser.replacement_unique_advertiser.advertiser_domains`,
      )
      .then(
        (successResponse) => {
          const advertisers = successResponse.data.map((x) => Advertiser.fromApi(x));
          successCallback(advertisers);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  async getAdvertisersAsync({ queryParams, cancelTokenSource = null }) {
    queryParams.expand = [
      ...(queryParams.expand == null ? [] : queryParams.expand),
      'unique_advertiser.advertiser_domains,unique_advertiser.replacement_unique_advertiser.advertiser_domains',
    ];
    const response = await this.axiosInstance
      .get(advertisersEndpoint, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Advertiser);
  }

  async getTTDAdvertisersAsync({ queryParams, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(TTDAdvertisersEndpoint, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Advertiser);
  }

  async getAllAdvertisersWithBuyerWithAdvertiserIDs({ queryParams, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${advertisersWithBuyersEndpoint}?$expand=unique_advertiser`, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Advertiser);
  }

  postAdvertiser(advertiser, successCallback, errorCallback) {
    this.axiosInstance.post(advertisersEndpoint, [advertiser.toApi()]).then(
      (successResponse) => {
        successCallback(Advertiser.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putAdvertiser(advertiser, successCallback, errorCallback) {
    this.axiosInstance.put(`${advertisersEndpoint}${advertiser.id}`, advertiser.toApi()).then(
      (successResponse) => {
        successCallback(Advertiser.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getUniqueAdvertisersWithBuyer({ queryParams, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${uniqueAdvertiserWithBuyersEndpoint}`, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, UniqueAdvertiser);
  }

  //
  // Accounts
  //
  getAccounts(successCallback, errorCallback) {
    this.axiosInstance.get(accountsEndpoint).then(
      (successResponse) => {
        successCallback(successResponse.data.map((x) => Account.fromApi(x)));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getAccountsAsync({ queryParams, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(accountsEndpoint, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Account);
  }

  postAccount(account, successCallback, errorCallback) {
    this.axiosInstance.post(accountsEndpoint, [account.toApi()]).then(
      (successResponse) => {
        successCallback(Account.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putAccount(account, successCallback, errorCallback) {
    this.axiosInstance.put(`${accountsEndpoint}${account.id}`, account.toApi()).then(
      (successResponse) => {
        successCallback(Account.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // Alerts
  //
  getDashboardAlert(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${alertsEndpoint}dashboardalert`,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getLoginAlert(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${alertsEndpoint}loginalert`,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) {
            return;
          }
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          if (errorResponse == null) {
            return;
          }
          handleError(errorResponse, errorCallback);
        },
      );
  }

  //
  // Audience files
  //
  async uploadAudienceFile({ demographicID, file }) {
    const data = new FormData();
    data.append('file', file);

    const successResponse = await this.axiosInstance
      .post(`${audienceDataEndpoint}${demographicID}`, data)
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    return successResponse.data;
  }

  //
  // Avails files
  //
  async uploadAvailsFile({ file }) {
    const data = new FormData();
    data.append('file', file);

    const successResponse = await this.axiosInstance.post(`${availsImportEndpoint}upload`, data).catch((err) => {
      throw ApiUtils.getError(err);
    });
    return successResponse.data;
  }

  //
  // Buildings
  //
  async getBuildingsAtCoordinates(coordinates) {
    const successResponse = await this.axiosInstance
      .post(`${buildingsEndpoint}atcoordinates`, coordinates)
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    return successResponse.data;
  }

  //
  // Buyers
  //
  getBuyer(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${buyersEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(Organization.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getBuyers(successCallback, errorCallback) {
    this.axiosInstance.get(buyersEndpoint).then(
      (successResponse) => {
        successCallback(successResponse.data.map((x) => Organization.fromApi(x)));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // Creatives
  //
  putCreativeState(creative, successCallback, errorCallback) {
    this.axiosInstance.put(`${creativesEndpoint}${creative.id}/`, creative.toApiStateChange()).then(
      (successResponse) => {
        successCallback(Creative.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async putCreativeApproval(creative: Creative, addEmptyInventoryTargeting: boolean = false) {
    const successResponse = await this.axiosInstance
      .put(
        `${creativesEndpoint}${creative.id}/mediaownerapprovals`,
        creative.toApiStateChange(addEmptyInventoryTargeting),
      )
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return Creative.fromApi(successResponse.data);
  }

  //
  // CreativePrefetchSize
  //
  approveCreativePrefetchSizes(creative, sizes, successCallback, errorCallback) {
    this.axiosInstance
      .post(`${creativePrefetchSizeEndpoint}approve`, {
        creative_id: creative.id,
        sizes,
      })
      .then(
        () => {
          successCallback(true);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  rejectCreativePrefetchSizes(creative, sizes, successCallback, errorCallback) {
    this.axiosInstance
      .post(`${creativePrefetchSizeEndpoint}reject`, {
        creative_id: creative.id,
        sizes,
      })
      .then(
        () => {
          successCallback(true);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  //
  // CreativeFiles
  //
  deleteCreativeFile(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${creativeFilesEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getCreativeFiles(queryParams, successCallback, errorCallback) {
    let requestUrl = `${creativesEndpoint}${queryParams.parentID}/creativefiles`;
    if (queryParams.parentID === null) {
      requestUrl = creativeFilesEndpoint;
    }
    this.axiosInstance
      .request({
        url: requestUrl,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(ApiUtils.getResult(successResponse, CreativeFile));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  postCreativeFile(creativeFile, successCallback, errorCallback) {
    this.axiosInstance.post(creativeFilesEndpoint, [creativeFile.toApi()]).then(
      (successResponse) => {
        successCallback(CreativeFile.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putCreativeFile(creativeFile, successCallback, errorCallback) {
    this.axiosInstance.put(`${creativeFilesEndpoint}${creativeFile.id}/`, creativeFile.toApi()).then(
      (successResponse) => {
        successCallback(CreativeFile.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  updateCreativeFilePrefetchApprovalState(creativeFileIDs, newState, successCallback, errorCallback) {
    const updates = creativeFileIDs.reduce((r, v) => {
      r[v.toString()] = { prefetch_approval: newState };
      return r;
    }, {});

    this.axiosInstance
      .patch(`${creativeFilesEndpoint}?$expandstrict=&$select=creativefile_id,prefetch_approval`, {
        to_update: updates,
      })
      .then(
        (successResponse) => {
          const result = successResponse.data.updated_entities.reduce((r, v) => {
            r[v.creativefile_id] = v.prefetch_approval;
            return r;
          }, {});

          successCallback(result);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  uploadCreativeFile(payload, creativeId, successCallback, errorCallback) {
    const data = new FormData();
    data.append('file', payload);

    this.axiosInstance.post(`${creativeFilesEndpoint}uploadforcreative/${creativeId}`, data).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  /**
   * Letterbox the creative file
   * @params payload {url: string, name: string, dimensions: number[][]}[]
   */
  async resizeCreativeFile(payload, successCallback, errorCallback) {
    try {
      const res = await this.axiosInstance.post('creatives/resize', payload);
      successCallback(res.data?.map((x) => ({ ...x, url: `data:${x.format};base64,${x.base64}` })) || []);
    } catch (error) {
      errorCallback(error);
    }
  }

  getCreativeLineItemMissingDimensions(creativeIDs, successCallback, errorCallback) {
    this.axiosInstance
      .get(`creatives/missinglineitemdimensions?creative_ids=${creativeIDs.join(',')}&with_unit_ids=true`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  //
  // CreativeProperties
  //
  deleteCreativeProperty(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${creativePropertiesEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getCreativeProperties(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${creativesEndpoint}${queryParams.parentID}/creativeproperties`,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(ApiUtils.getResult(successResponse, CreativeProperty));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  postCreativeProperty(creativeProperty, successCallback, errorCallback) {
    this.axiosInstance.post(creativePropertiesEndpoint, [creativeProperty.toApi()]).then(
      (successResponse) => {
        successCallback(CreativeProperty.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putCreativeProperty(creativeProperty, successCallback, errorCallback) {
    this.axiosInstance.put(`${creativePropertiesEndpoint}${creativeProperty.id}/`, creativeProperty.toApi()).then(
      (successResponse) => {
        successCallback(CreativeProperty.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // Dashboards
  //
  async getLatestPlays({ params, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .request({
        url: `${reportsEndpoint}latestplays`,
        method: 'post',
        data: params,
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data.map((x) => AdServerPlay.fromApi(x));
  }

  //
  // Discount Types
  //
  getDiscountTypes(successCallback, errorCallback) {
    this.axiosInstance.get(discountTypesEndpoint).then(
      (successResponse) => {
        successCallback(successResponse.data.map((x) => DiscountType.fromApi(x)));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // GeofenceGroups
  //
  deleteGeofenceGroup(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${geofenceGroupsEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // async getGeofenceGroup(id, successCallback, errorCallback) {
  //   this.axiosInstance.get(`${geofenceGroupsEndpoint}${id}`)
  //     .then((successResponse) => {
  //       successCallback(GeofenceGroup.fromApi(successResponse.data));
  //     }, (errorResponse) => {
  //       errorCallback(errorResponse);
  //       console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
  //     });
  // }

  async getGeofenceGroup({ id, cancelTokenSource = null, loadGeofences = true }) {
    const response = await this.axiosInstance
      .get(`${geofenceGroupsEndpoint}${id}?$expand=last_refreshed_on`, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    const geofenceGroup = GeofenceGroup.fromApi(response.data);

    if (!loadGeofences) {
      return geofenceGroup;
    }

    //
    // Fetch geofences.
    //
    const pageSize = 100;
    const filterCollection = FilterCollection.for('geofencegroup_id', [id]);
    const queryParams = QueryParams.for({
      top: pageSize,
      filterCollection,
    });
    const geofencesResponse = await this.axiosInstance
      .get(geofencesEndpoint, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    const result = ApiUtils.getResult(geofencesResponse, Geofence);
    geofenceGroup.geofences = result.items;

    if (result.totalCount > pageSize) {
      const queryParamsCollection = [];
      for (let i = pageSize; i < result.totalCount; i += pageSize) {
        queryParamsCollection.push(
          QueryParams.for({
            skip: i,
            top: pageSize,
            filterCollection,
          }).toObject(),
        );
      }
      const maxParallelQueries = 3;
      const processNextQueryBatch = async (batchIndex = 0) => {
        const batchPromises = queryParamsCollection
          .slice(batchIndex * maxParallelQueries, (batchIndex + 1) * maxParallelQueries)
          .map((qp) =>
            this.axiosInstance.get(geofencesEndpoint, {
              params: qp,
              cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
            }),
          );
        const batchResponses = await Promise.all(batchPromises);
        const batchResults = batchResponses.map((x) => ApiUtils.getResult(x, Geofence));
        geofenceGroup.geofences = [...geofenceGroup.geofences, ...batchResults.map((x) => x.items).flat()];
        if (batchIndex * maxParallelQueries < queryParamsCollection.length) {
          await processNextQueryBatch(batchIndex + 1);
        }
      };

      await processNextQueryBatch();
    }
    geofenceGroup.apiObject.geofences = geofenceGroup.geofences.map((x) => x.toApi());

    return geofenceGroup;
  }

  postGeofenceGroup(geofenceGroup, successCallback, errorCallback) {
    this.axiosInstance.post(geofenceGroupsEndpoint, [geofenceGroup.toApi()]).then(
      (successResponse) => {
        successCallback(GeofenceGroup.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putGeofenceGroup(geofenceGroup, successCallback, errorCallback) {
    this.axiosInstance
      .put(`${geofenceGroupsEndpoint}${geofenceGroup.id}/`, geofenceGroup.toApi ? geofenceGroup.toApi() : geofenceGroup)
      .then(
        (successResponse) => {
          successCallback(GeofenceGroup.fromApi(successResponse.data));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  async getGeofenceUnitMapping({ geofenceGroupID, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${geofenceGroupsEndpoint}${geofenceGroupID}/geofenceidunitidmapping/`, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data;
  }

  async getGeofenceSiteMappingForSitesPOST({ geofenceGroupID, siteIDs, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .post(
        `${geofenceGroupsEndpoint}${geofenceGroupID}/geofencemappingforsites/`,
        {
          site_ids: siteIDs,
        },
        {
          cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
        },
      )
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data;
  }

  async getGeofenceUnitMappingForUnitsPOST({ geofenceGroupID, unitIDs, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .post(
        `${geofenceGroupsEndpoint}${geofenceGroupID}/geofencemappingforunits/`,
        {
          unit_ids: unitIDs,
        },
        {
          cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
        },
      )
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data;
  }

  async getGeofenceSiteMapping({ geofenceGroupID, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${geofenceGroupsEndpoint}${geofenceGroupID}/geofenceidsiteidmapping/`, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data;
  }

  async previewGeofenceGroup({
    geofenceGroup,
    excludeInactive = true,
    excludeUnavailable = true,
    useSaved = false,
    buyFromDealsEnabled = false,
    cancelTokenSource = null,
  }) {
    const params = {
      exclude_inactive: excludeInactive,
      exclude_unavailable: excludeUnavailable,
      use_saved: useSaved,
      buy_from_deals_enabled: buyFromDealsEnabled,
      requested_for_utc: geofenceGroup?.createdOnUtc ?? DateTime.utc(),
    };
    let data = null;
    if (excludeUnavailable && useSaved) {
      data = geofenceGroup.geofences.map((x) => x.toApiPreviewScreens());
    } else {
      data = geofenceGroup.geofences.map((x) => x.toApi());
    }
    const response = await this.axiosInstance
      .request({
        url: `${geofencesEndpoint}preview_inventory/`,
        method: 'post',
        params,
        data,
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data.map((x) => UnitInfo.fromApi(x));
  }

  patchGeofences(toAdd, toUpdate, toDelete, successCallback, errorCallback) {
    const toUpdateAsDict = {};
    toUpdate.forEach((x) => {
      toUpdateAsDict[x.id] = x.toApi();
    });

    const data = {
      to_add: toAdd.map((x) => x.toApi()),
      to_update: toUpdate.length > 0 ? toUpdateAsDict : undefined,
      to_delete: toDelete.length > 0 ? toDelete.map((x) => x.id) : undefined,
    };
    this.axiosInstance.patch(geofencesEndpoint, data).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getGenericGeofenceGroupTypeCountries({ queryParams = QueryParams.all(), cancelTokenSource = null } = {}) {
    const response = await this.axiosInstance
      .get(genericGeofenceTypeCountryEndpoint, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, GenericGeofenceTypeCountry);
  }

  async getGenericGeofences({ queryParams = QueryParams.all(), cancelTokenSource = null } = {}) {
    const response = await this.axiosInstance
      .get(genericGeofenceEndpoint, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Geofence, Geofence.fromGenericGeofence);
  }

  async getGenericGeofencesWithinRadius({ radius, lat, lng, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${genericGeofenceEndpoint}withinradius`, {
        params: {
          radius,
          lat,
          lon: lng,
        },
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Geofence, Geofence.fromGenericGeofence);
  }

  //
  // Data Providers
  //
  async getDataProviders(params?: QueryParams) {
    return this.axiosInstance
      .get(dataProvidersEndpoint, {
        params,
      })
      .then((res) => res.data.map((x) => DataProvider.fromApi(x)));
  }

  //
  // Data Marketplaces
  async getDataMarketplaces() {
    return this.axiosInstance.get(dataMarketplaceEndpoint).then((res) => {
      return res.data.map((x) => DataMarketplace.fromApi(x));
    });
  }

  //
  // Epsilon segments
  //
  getEpsilonSegmentsByOrg(dataProviderId, queryParams, successCallback, errorCallback) {
    if (queryParams.include == null) {
      // To remove receiving CPM when making the call

      queryParams.include = ExternalSegment.includeAllParameters;
    }
    this.axiosInstance
      .request({
        url: `${externalSegmentsByOrganizationEndpoint}${dataProviderId}`,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          const responseData = successResponse.data.map((x) => ExternalSegment.fromApi(x));
          if (queryParams.returnsCount) {
            successCallback({
              totalCount: parseInt(successResponse.headers['x-total-count'], 10),
              data: responseData,
            });
          } else {
            successCallback(responseData);
          }
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  //
  // External Demographics
  //
  getExternalDemographics(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: externalDemographicsEndpoint,
        params: queryParams !== null ? queryParams.toObject() : undefined,
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(successResponse.data.map((x) => ExternalDemographic.fromApi(x)));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  // External Exchanges

  public getExternalSSPs = this.apiUtils.createGetAllFunction(`${externalSSPsEndpoint}`, ExternalSSP);

  //
  // OrganizationDSPs

  public getOrganizationDSPs = this.apiUtils.createGetAllFunction(
    `${organizationDSPsEndpoint}?$expand=dsp,organization`,
    OrganizationDSP,
  );

  // This is used to get org_dsp_ids for a buyer org
  getOrganizationDSPIDs(successCallback, errorCallback, external = false) {
    let params = {};
    if (external) {
      params = { is_external: external };
    }

    this.axiosInstance.get(`${organizationDSPsEndpoint}`, { params }).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  public async orgIsLinkedToDsp(dspCode) {
    const response = await this.axiosInstance.get(
      `${organizationDSPsEndpoint}?$filter=dsp.external_exchange.code eq '${dspCode}'&is_external=true`,
    );
    return response;
  }

  async getOrganizationExternalExchanges() {
    const orgAppState = rootState?.orgApp;
    const organization = orgAppState?.organization;
    const organizationDSPBuyersList = orgAppState.ReferenceData.organizationDSPBuyersList || [];
    const dvBuyers = organizationDSPBuyersList.filter(
      (x) => x.dsp_name.toLowerCase().includes('dv360') && x.active && x.is_openrtb,
    );
    const xandrBuyers = organizationDSPBuyersList.filter(
      (x) => x.dsp_name.toLowerCase().includes('xandr') && x.active && x.is_openrtb,
    );
    const bellBuyers = organizationDSPBuyersList.filter(
      (x) => x.dsp_name.toLowerCase().includes('bell') && x.active && x.is_openrtb,
    );
    const isDV360BuyerExist = dvBuyers.length > 0;
    const isXANDRBuyerExist = xandrBuyers.length > 0;
    const isBellBuyerExist = bellBuyers.length > 0;
    let dspName = i18n.global.t('messages.hivestackDSPBuyersOnly');
    let logoURL = 'https://dgg3rnz8nudgw.cloudfront.net/dsp_751866b6-4bb0-4eff-9c3e-612be20a2612.png';
    if (organization?.name === 'LIVE BOARD') {
      dspName = i18n.global.t('messages.liveBoardDSPBuyersOnly');
      logoURL = 'https://dgg3rnz8nudgw.cloudfront.net/dsp_d7558f14-8e67-4f70-884f-481d0a737d85.png';
    }
    const hivestackDSP = {
      code: 'INTERNAL',
      external_exchange_id: -1,
      is_openrtb: false,
      logo_url: logoURL,
      name: dspName,
      isDisabled: false,
    };
    let DV360Exchange = {
      code: 'DV360_RTB',
      external_exchange_id: -2,
      is_openrtb: true,
      logo_url: 'https://dgg3rnz8nudgw.cloudfront.net/extExchange_93b7f9f9-190c-42ca-a82f-9f5bf3f49100.png',
      name: 'DV360',
      isDisabled: false,
    };
    let XANDRExchange = {
      code: 'XANDR',
      external_exchange_id: -3,
      is_openrtb: true,
      logo_url: 'https://cloudfront-ax-master.s3.amazonaws.com/dsp-xandr-logo.png',
      name: 'Xandr',
      isDisabled: false,
    };
    try {
      const response = await this.axiosInstance.get(`${organizationsEndpoint}external_exchanges`);
      XANDRExchange = response.data.find((x) => x.code.toLowerCase().includes('xandr')) || XANDRExchange;
      DV360Exchange = response.data.find((x) => x.code.toLowerCase().includes('dv360')) || DV360Exchange;
      const isBellExchangeExist = response.data.find((x) => x.code.toLowerCase().includes('bell'));
      const data = response.data.filter(
        (x) => !x.code.toLowerCase().includes('xandr') && !x.code.toLowerCase().includes('dv360'),
      );
      data.unshift(hivestackDSP);

      // If XANDR OR DV360 Buyers exist, move it next to TTD (TTD sorting is done in BE)
      if (isXANDRBuyerExist) {
        data.splice(data.findIndex((x) => x.code.includes('TTD')) + 1, 0, XANDRExchange);
      }
      if (isDV360BuyerExist) {
        data.splice(data.findIndex((x) => x.code.includes('TTD')) + 1, 0, DV360Exchange);
      }
      if (!isBellBuyerExist && isBellExchangeExist) {
        data.splice(
          data.findIndex((x) => x.code.toLowerCase().includes('bell')),
          1,
        );
      }
      return data.map((x) => ({
        ...ExternalExchange.fromApi(x),
        isDisabled: false,
      }));
    } catch (error) {
      throw ApiUtils.getError(error);
    }
  }

  //
  // LineItems
  //
  /**
   * Gets RTB metrics (requests #, no bids #, failures #) for line item for start -> end
   * @param lineItemId Integer line item id
   * @param params object {
   *     start: required, int unix timestamp in seconds
   *     end: required, int unix timestamp in seconds
   * }
   * @param successCallback callable with response object {
   *     'line_item_id': {
   *         'requests': 0,  // requests # for start -> end
   *         'no_bids': 0,  // no bids # for start -> end
   *         'failures': 0,  // failures # for start -> end
   *     },
   * }
   * @param errorCallback Error object
   */
  getLineItemDefaultRTBMetrics(lineItemId, params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`${lineItemsEndpoint}metrics/${lineItemId}`, {
        params: {
          start: Math.round(params.start.toMillis() / 1000),
          end: Math.round(params.end.toMillis() / 1000),
          keep_series: params.keep_series,
        },
      })
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  /**
   * Gets RTB metrics (requests #, no bids #, failures #) for line item for target day
   * @param lineItemId Integer line item id
   * @param day Day in the form "%Y-%m-%d%z". Examples: 2018-10-20-0500 for 20th october 2018, EST; 2018-10-20+0100 for 20th october 2018, CET
   * @param params
   * @param successCallback callable with response object {
   *     'line_item_id': {
   *         'requests': 0,  // requests # for day
   *         'no_bids': 0,  // no bids # for day
   *         'failures': 0,  // failures # for day
   *     },
   * }
   * @param errorCallback
   */
  getLineItemRTBMetricsForDay(lineItemId, day, params, successCallback, errorCallback) {
    this.axiosInstance.get(`${lineItemsEndpoint}metrics/${lineItemId}/${day.toISODate()}+0000`, { params }).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  deleteLineItem(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${lineItemsEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAllLineItems(successCallback, errorCallback) {
    this.axiosInstance.get(lineItemsEndpoint).then(
      (successResponse) => {
        successCallback(successResponse.data.map((x) => LineItem.fromApi(x)));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getLineItems(queryParams, successCallback, errorCallback) {
    if (queryParams.parentID === null) {
      this.axiosInstance
        .request({
          url: lineItemsEndpoint,
          params: queryParams !== null ? queryParams.toObject() : undefined,
          cancelToken: queryParams.cancelTokenSource != null ? queryParams.cancelTokenSource.token : null,
        })
        .then(
          (successResponse) => {
            if (successResponse === undefined) {
              return;
            }
            successCallback(ApiUtils.getResult(successResponse, LineItem));
          },
          (errorResponse) => {
            errorCallback(errorResponse);
            if (!(errorResponse instanceof CanceledError)) {
              console.error(
                errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error',
              );
            }
          },
        );
    } else {
      this.axiosInstance
        .request({
          url: `${campaignsEndpoint}${queryParams.parentID}/lineitems`,
          params: queryParams !== null ? queryParams.toObject() : undefined,
          cancelToken: queryParams.cancelTokenSource != null ? queryParams.cancelTokenSource.token : null,
        })
        .then(
          (successResponse) => {
            if (successResponse === undefined) {
              return;
            }
            successCallback(ApiUtils.getResult(successResponse, LineItem));
          },
          (errorResponse) => {
            errorCallback(errorResponse);
            if (!(errorResponse instanceof CanceledError)) {
              console.error(
                errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error',
              );
            }
          },
        );
    }
  }

  getLineItemsPerCreative(queryParams, successCallback, errorCallback) {
    if (queryParams.parentID === null) {
      this.axiosInstance
        .request({
          url: lineItemsEndpoint,
          params: queryParams !== null ? queryParams.toObject() : undefined,
          cancelToken: queryParams.cancelTokenSource != null ? queryParams.cancelTokenSource.token : null,
        })
        .then(
          (successResponse) => {
            if (successResponse === undefined) {
              return;
            }
            successCallback(ApiUtils.getResult(successResponse, LineItem));
          },
          (errorResponse) => {
            errorCallback(errorResponse);
            if (!(errorResponse instanceof CanceledError)) {
              console.error(
                errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error',
              );
            }
          },
        );
    } else {
      this.axiosInstance
        .request({
          url: `${creativesEndpoint}${queryParams.parentID}/lineitems`,
          params: queryParams !== null ? queryParams.toObject() : undefined,
          cancelToken: queryParams.cancelTokenSource.token,
        })
        .then(
          (successResponse) => {
            if (successResponse === undefined) {
              return;
            }
            successCallback(ApiUtils.getResult(successResponse, LineItem));
          },
          (errorResponse) => {
            handleError(errorResponse, errorCallback);
          },
        );
    }
  }

  getCreativesPerLineItem(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${lineItemsEndpoint}${queryParams.parentID}/creatives`,
        params: queryParams !== null ? queryParams.toObject() : undefined,
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          const creatives = ApiUtils.getResult(successResponse, Creative);
          successCallback(creatives);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getLineItem(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${lineItemsEndpoint}${id}?$expand=${LineItem.expands.join(',')}`).then(
      (successResponse) => {
        successCallback(LineItem.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getLineItemSummaryDeliveryStats(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${reportsEndpoint}dashboard/lineitem/${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  // Reach and frequency
  getReachAndFrequencyOverall(params, cancelTokenSource, successCallback, errorCallback) {
    this.axiosInstance
      .post(reachAndFrequencyOverall, params, {
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse) {
            successCallback(ReachAndFrequencyStatistics.fromApi(successResponse?.data?.measures_summaries?.overall));
          }
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  // entityType is a string and can be one of the values from ReachAndFrequencyReportsType
  async getReachAndFrequencyReports(entityType, entityId, params = {}, successCallback, errorCallback) {
    try {
      const response = await this.axiosInstance.get(
        `${reachAndFrequencyReportEndpoint}/${entityType}/${entityId}`,
        params,
      );
      return successCallback(response.data.map((report) => ReachAndFrequencyReport.fromAPI(report)));
    } catch (e) {
      return handleError(e, errorCallback);
    }
  }

  getLineItemSummaryPanel(params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`dashboards/lineitems/${params.id}/summary${params.start}${params.end}?timezone=UNIT_LOCAL`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getLineItemDetails(params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`dashboards/lineitems/${params.id}/delivery${params.start}${params.end}?timezone=${params.timezone}`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getLineItemSites(params, successCallback, errorCallback) {
    const url = params.path
      ? params.path.replace('api/ui/org/', '')
      : `dashboards/lineitems/${params.id}/summary/sites${params.start}${params.end}?timezone=UNIT_LOCAL`;
    this.axiosInstance.get(url).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getLineItemUnits(params, successCallback, errorCallback) {
    const url = params.path
      ? params.path.replace('api/ui/org/', '')
      : `dashboards/lineitems/${params.id}/summary/units${params.start}${params.end}?timezone=UNIT_LOCAL`;
    this.axiosInstance.get(url).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getLineItemTimeline(params, successCallback, errorCallback) {
    this.axiosInstance.get(`dashboards/lineitems/${params.id}/delivery/sites?timezone=UNIT_LOCAL`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getCampaignTimeline(params, successCallback, errorCallback) {
    const dates =
      params.start_date && params.end_date ? `&start_date=${params.start_date}&end_date=${params.end_date}` : '';
    this.axiosInstance
      .post(
        `dashboards/campaigns/shared/${params.uuid}/delivery/sites?timezone=${params.timezone}&as_array=true&skip_empties=${params.skip_empties}${dates}`,
        params,
      )
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getLineItemHourlyBySite(params, successCallback, errorCallback) {
    const queryString = params.queryString || '';
    const url = params.path
      ? `${params.path.replace('api/ui/org/', '')}${queryString}`
      : `dashboards/lineitems/${params.id}/summary/hourly/sites/${params.date}?timezone=UNIT_LOCAL${queryString}&$skip=${params.skip ?? 0}`;
    this.axiosInstance.get(url).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getLineItemHourlyByUnit(params, successCallback, errorCallback) {
    const queryString = params.queryString || '';
    const url = params.path
      ? `${params.path.replace('api/ui/org/', '')}${queryString}`
      : `dashboards/lineitems/${params.id}/summary/hourly/units/${params.date}?timezone=UNIT_LOCAL${queryString}`;
    this.axiosInstance.get(url).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getIsDealBought(params, successCallback, errorCallback) {
    const url = `packages/${params.id}/isbought`;
    this.axiosInstance.get(url).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getDeliveryLineItems(queryParams, date, successCallback, errorCallback) {
    this.axiosInstance
      .get(
        `dashboards/campaigns/${queryParams.parentID}/summary/lineitems/${date.start}/${date.end}?timezone=UNIT_LOCAL`,
      )
      .then(
        (successResponse) => {
          successResponse.data.totals = {
            ...successResponse.data.totals,
            type: 'data-totals',
            lineitem_name: 'totals',
            lineitem_id: 'totals_id',
          };
          successResponse.data.lineitems.push(successResponse.data.totals);
          successCallback(ApiUtils.getDeliveryResult(successResponse, LineItemDelivery));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  async getDailyLineItemHourlyDeliveryStats(id, date) {
    const successResponse = await this.axiosInstance
      .get(`${reportsEndpoint}dashboard/lineitem/daily/${id}/${date.toISODate()}`)
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async getAdvancedReportExecution(id) {
    const successResponse = await this.axiosInstance
      .get(`${advancedReportExecutionsEndpoint}/${id}`)
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });
    if (successResponse == null) {
      return null;
    }

    return AdvancedReportExecution.fromApi(successResponse.data);
  }

  getDemographicUnitsWithData(params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`${demographicsEndpoint}${params.id}/units/`, {
        params: {
          from_date: toServerDate(params.fromDate),
          to_date: toServerDate(params.toDate),
        },
      })
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getCPMForTargets(params, successCallback, errorCallback) {
    const { cancelTokenSource, ...apiParams } = params;
    this.axiosInstance
      .request({
        url: `${lineItemsEndpoint}calculatecpm`,
        method: 'post',
        data: apiParams,
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : undefined,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) {
            return;
          }
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  async generateAdvancedReportExecution({ params }) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${advancedReportExecutionsEndpoint}`,
        method: 'post',
        data: params,
        // cancelToken: cancelTokenSource != null ? cancelTokenSource.token : undefined,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  getLineItemCPM(lineItem, successCallback, errorCallback) {
    this.axiosInstance.get(`${mediaOwnersEndpoint}/lineitem/${lineItem.id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getCadenceReports({ id, period, cancelTokenSource = null }) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${lineItemsEndpoint}${id}/cadencereporting?granularity=${period}`,
        method: 'get',
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    return successResponse.data;
  }

  postLineItem(lineItem, successCallback, errorCallback) {
    this.axiosInstance.post(lineItemsEndpoint, [lineItem.toApi()]).then(
      (successResponse) => {
        successCallback(LineItem.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putLineItemState(lineItem, successCallback, errorCallback) {
    this.axiosInstance.put(`${lineItemsEndpoint}${lineItem.id}/`, lineItem.toApiStateChange()).then(
      (successResponse) => {
        successCallback(LineItem.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putLineItemRunState(lineItem, successCallback, errorCallback) {
    this.axiosInstance.put(`${lineItemsEndpoint}${lineItem.id}/`, lineItem.toApiRunStateChange()).then(
      (successResponse) => {
        successCallback(LineItem.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getUnitsForTargets({ params, cancelTokenSource = null }) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${lineItemsEndpoint}unitsfortargets`,
        method: 'post',
        data: params,
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async getUnitAudit({ id, cancelTokenSource = null }) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${unitsEndpoint}${id}/audit`,
        method: 'get',
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    return new AuditTrail(successResponse.data, 'Unit');
  }

  async getUnitAdRequestPerHour({ id, params, cancelTokenSource = null }) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${unitsEndpoint}${id}/callcount`,
        params,
        method: 'get',
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    return successResponse.data;
  }

  async getUnitFillRate(params: { id: string; fromDate: string; toDate: string }): Promise<FillRate[]> {
    const { id, fromDate, toDate } = params;

    try {
      const response = await this.axiosInstance.get(`adrequests/units/${id}/fillrate`, {
        params: { from_datetime: fromDate, to_datetime: toDate, resolution: 'hourly' },
      });
      return response.data.map((fillRate) => FillRate.fromApi(fillRate));
    } catch (errorResponse) {
      throw ApiUtils.getError(errorResponse);
    }
  }

  getGeocodedUnitsAndSitesForTargets(params, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${
          params.type === HeatmapType.CAMPAIGN ? lineItemsEndpoint : plansEndpoint
        }geocodedunitsandsitesfortargets`,
        method: 'post',
        data: params,
        cancelToken: params.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(successResponse.data.map((x) => GeocodedLocation.fromApi(x)));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getMissingCreativeDimensionsForTargets(params, successCallback, errorCallback) {
    this.axiosInstance.post(`${lineItemsEndpoint}missingcreativedimensionsfortargets`, params).then(
      (successResponse) => {
        const ids = Object.keys(successResponse.data);
        const creativesUnitDimensions = ids.map(
          (id) =>
            new CreativeUnitDimensions(
              parseInt(id, 10),
              successResponse.data[id]
                .map((x) => UnitDimension.fromApi(x))
                .sort((a, b) => a.width * 1000 + a.height - (b.width * 1000 + b.height)),
            ),
        );
        successCallback(creativesUnitDimensions);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAvails(params, successCallback, errorCallback) {
    this.axiosInstance.post(`${availsEndpoint}checkavails`, params).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAvailsByUnitPost(params, successCallback, errorCallback) {
    const queryParams = params.checkForOptimizedImpressions ? '?optimize_impressions=true' : '';
    this.axiosInstance
      .request({
        url: `${availsEndpoint}checkavailsbyunit${queryParams}`,
        method: 'post',
        data: params,
        cancelToken: params.cancelTokenSource != null ? params.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse != null) {
            successCallback(AvailsProgress.fromApi(successResponse.data));
          }
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  getAvailsByUnit(lineItemId, successCallback, errorCallback) {
    this.axiosInstance.get(`${availsEndpoint}checkavailsbyunit/${lineItemId}`).then(
      (successResponse) => {
        successCallback(AvailsProgress.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAvailsExecutionById(availsExecutionId, successCallback, errorCallback) {
    this.axiosInstance.get(`${availsEndpoint}checkavailsexecution/${availsExecutionId}`).then(
      (successResponse) => {
        successCallback(AvailsProgress.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getAvailsDeliveryForecastByPost(params, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: deliveryForecastEndpoint,
        method: 'post',
        data: params,
        cancelToken: params.cancelTokenSource != null ? params.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse != null) {
            successCallback(DeliveryForecastProgress.fromApi(successResponse.data[0]));
          }
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  getAvailsDeliveryForecastExecutionById(availsExecutionId, successCallback, errorCallback) {
    this.axiosInstance.get(`${deliveryForecastEndpoint}/${availsExecutionId}`).then(
      (successResponse) => {
        successCallback(DeliveryForecastProgress.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  approveLineItem(lineItem, successCallback, errorCallback) {
    this.axiosInstance
      .put(`${lineItemsEndpoint}${lineItem.id}/mediaownerapprovals`, {
        state: LineItemStatus.APPROVED,
      })
      .then(
        () => {
          successCallback();
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  rejectLineItem(lineItemAndComment, successCallback, errorCallback) {
    this.axiosInstance
      .put(`${lineItemsEndpoint}${lineItemAndComment.lineItem.id}/mediaownerapprovals`, {
        state: LineItemStatus.REJECTED,
        approval_comment: lineItemAndComment.comment,
      })
      .then(
        () => {
          successCallback();
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getLineItemIsApprovalRequired(params, successCallback, errorCallback) {
    this.axiosInstance
      .post(`${lineItemsEndpoint}isapprovalrequired`, {
        purchase_type: params.purchaseType,
        owner_ids: params.ownerIDs,
      })
      .then(
        (successResponse) => {
          successCallback(successResponse.data.approval_required);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getLineItemAudit(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${lineItemsEndpoint}${id}/audit`).then(
      (successResponse) => {
        const auditTrail = new AuditTrail(successResponse.data, 'LineItem');
        successCallback(auditTrail);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getUnitRatioStatuses({ lineItemIDs, cancelTokenSource = null }) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${lineItemsEndpoint}getratiostatuses`,
        params: { lineitem_ids: lineItemIDs.join(',') },
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async getUnitRatioStatusesForTargets({ queryParams, cancelTokenSource = null }) {
    const endPoint = queryParams.type === EntityType.PLANS ? plansEndpoint : lineItemsEndpoint;
    const successResponse = await this.axiosInstance
      .request({
        url: `${endPoint}getratiostatuses`,
        method: 'post',
        data: queryParams,
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async updateLineItemsRunState(lineItemIds, newState, successCallback, errorCallback) {
    const updates = lineItemIds.reduce((r, v) => {
      r[v.toString()] = { run_state: newState };
      return r;
    }, {});

    this.axiosInstance.patch(`${lineItemsEndpoint}`, { to_update: updates }).then(
      (successResponse) => {
        const result = successResponse.data.updated_entities.reduce((r, v) => {
          r[v.lineitem_id] = v.run_state;
          return r;
        }, {});

        successCallback(result);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async updateLineItemsState(lineItemIds, newState, successCallback, errorCallback) {
    const updates = lineItemIds.reduce((r, v) => {
      r[v.toString()] = { state: newState };
      return r;
    }, {});

    this.axiosInstance.patch(`${lineItemsEndpoint}`, { to_update: updates }).then(
      (successResponse) => {
        const result = successResponse.data.updated_entities.reduce((r, v) => {
          r[v.lineitem_id] = v.run_state;
          return r;
        }, {});

        successCallback(result);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // LineItemCreatives
  //
  getLineItemCreativeMissingDimensions(lineItemIDs, successCallback, errorCallback) {
    this.axiosInstance
      .get(`lineitems/missingcreativedimensions?lineitem_ids=${lineItemIDs.join(',')}&with_unit_ids=false`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  postLineItemCreatives(lineItemCreatives, successCallback, errorCallback) {
    this.axiosInstance
      .post(
        lineItemCreativesEndpoint,
        lineItemCreatives.map((x) => x.toApi()),
      )
      .then(
        (successResponse) => {
          successCallback(successResponse.data.map((x) => LineItemCreative.fromApi(x)));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  updateLineItemCreatives(lineItemCreatives) {
    const toUpdate = lineItemCreatives.reduce((mem, x) => {
      const apiModel = x.toApi();
      const mutatedMem = { ...mem };
      mutatedMem[`${apiModel.lineitemcreative_id}`] = apiModel;
      return mutatedMem;
    }, {});

    const payload = {
      to_update: toUpdate,
    };

    return this.axiosInstance
      .patch(`${lineItemCreativesEndpoint}?$expandstrict=[]`, payload)
      .then((successResponse) => successResponse.data.updated_entities.map((x) => LineItemCreative.fromApi(x)))
      .catch((errorResponse) => {
        console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        throw getErrorOld(errorResponse);
      });
  }

  deleteCreativeLineItems(creativeLineItem, successCallback, errorCallback) {
    this.axiosInstance
      .delete(`${creativesEndpoint}${creativeLineItem.creativeID}/lineitems/${creativeLineItem.lineItemID}`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  //
  // Locations
  //
  getCountries(successCallback, errorCallback) {
    this.axiosInstance.get(`${locationsEndpoint}?$filter=level eq 0`).then(
      (successResponse) => {
        successCallback(
          successResponse.data
            .map((x) => Location.fromApi(x))
            .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
        );
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getLocation({ id, includeShapes }, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${locationsEndpoint}${id}`,
        params: includeShapes
          ? {
              $expand: 'shapes',
            }
          : null,
      })
      .then(
        (successResponse) => {
          successCallback(Location.fromApi(successResponse.data));
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  getLocations(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: locationsEndpoint,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(
            successResponse.data
              .map((x) => Location.fromApi(x))
              .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
          );
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  async searchLocations({ queryParams, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${locationsEndpoint}search`, {
        params: queryParams,
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data.map((x) => Location.fromApi(x));
  }

  getChildrenLocations(location, networkIDs, ownerIDs, successCallback, errorCallback) {
    const params = {
      network_ids: (networkIDs || []).join(','),
      owner_ids: (ownerIDs || []).join(','),
    };
    this.axiosInstance
      .request({
        url: `${locationsEndpoint}${location.id}/children`,
        params,
      })
      .then(
        (successResponse) => {
          successCallback(
            successResponse.data
              .map((x) => Location.fromApi(x))
              .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
          );
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  treeSearchLocations(searchTerm, countryIDs, networkIDs, cancelTokenSource, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${locationsEndpoint}treesearchalllocations`,
        params: {
          searchterm: searchTerm,
          countryIDs: countryIDs.join(','),
          networkIDs: networkIDs != null ? networkIDs.join(',') : null,
        },
        cancelToken: cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback({
            count: successResponse.data.total_count,
            locations: successResponse.data.result
              .map((x) => Location.fromApi(x))
              .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
          });
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getCitiesWithinRadius(latitude, longitude, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${locationsEndpoint}citieswithinradius`,
        params: {
          latitude,
          longitude,
        },
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(
            successResponse.data
              .map((x) => Location.fromApi(x))
              .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
          );
        },
        (errorResponse) => {
          errorCallback(getErrorOld(errorResponse), errorResponse.response.status);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  //
  // Markets
  //
  getMarket(id, includeGeometry) {
    let columns = 'market_id,name,type,external_id,location_id';
    if (includeGeometry) {
      columns += ',geometry';
    }

    return this.axiosInstance
      .get(`${marketsEndpoint}${id}?$select=${columns}`)
      .catch((errorResponse) => {
        console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        const err: Error & { status? } = new Error(getErrorOld(errorResponse));
        err.status = errorResponse.response.status;
        throw err;
      })
      .then((successResponse) => Market.fromApi(successResponse.data));
  }

  //
  // MediaOwners
  //
  async getMediaOwners({ cancelTokenSource = null, planUuid = null, queryParams = null } = {}) {
    const endpoint = planUuid ? `/plans/${planUuid}/suppliers` : mediaOwnersEndpoint;
    const successResponse = await this.axiosInstance
      .get(endpoint, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
        params: queryParams?.toObject() ?? null,
      })
      .catch((err) => {
        console.error(JSON.stringify(err.response.data));
        throw ApiUtils.getError(err);
      });
    return successResponse.data.map((x) => Organization.fromApi(x));
  }

  //
  // Organization
  //
  getOrganizationTopScreenDimensions(successCallback, errorCallback) {
    const orgAppState = rootState?.orgApp;
    const { id } = orgAppState.organization;
    this.axiosInstance.get(`${organizationsEndpoint}${id}/units/topscreendimensions/`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // Organization settings
  //
  async getOrganizationSettings({ cancelTokenSource = null } = {}) {
    const successResponse = await this.axiosInstance
      .get(organizationSettingsEndpoint, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        console.error(JSON.stringify(err.response.data));
        throw ApiUtils.getError(err);
      });
    return MembershipSettings.fromApi(successResponse.data);
  }

  //
  // Packages
  //
  deletePackage(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${packagesEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getPackages({ queryParams }) {
    const endpoint = queryParams.customQueryParams?.isMultiDeal
      ? `${packagesEndpoint}multidealpackages/`
      : packagesEndpoint;
    const response = await this.axiosInstance
      .request({
        url: endpoint,
        params: queryParams.toObject(),
        cancelToken: queryParams?.cancelTokenSource ? queryParams.cancelTokenSource?.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Package);
  }

  getPackage(id, isProgrammaticGuaranteed, successCallback, errorCallback) {
    const params: { $expand; allow_previous_revisions? } = {
      $expand: Package.expands.join(','),
    };
    if (isProgrammaticGuaranteed) {
      params.allow_previous_revisions = true;
    }
    this.axiosInstance
      .request({
        url: `${packagesEndpoint}${id}`,
        params,
      })
      .then(
        (successResponse) => {
          successCallback(Package.fromApi(successResponse.data));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getPackageAudit(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${packagesEndpoint}${id}/audit`).then(
      (successResponse) => {
        const auditTrail = new AuditTrail(successResponse.data, 'Package');
        successCallback(auditTrail);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getPackageCreatives(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${packagesEndpoint}${id}/creatives`).then(
      (successResponse) => {
        successCallback(successResponse.data.map((x) => Creative.fromApi(x)));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getPackageDetails(params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`dashboards/packages/${params.id}/delivery/${params.start}/${params.end}?timezone=UNIT_LOCAL`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getCuratedPackageDetails(params, successCallback, errorCallback) {
    this.axiosInstance
      .get(
        `dashboards/curate/packages/${params.id}/revenue?start_date=${params.start}&end_date=${params.end}&timezone=UNIT_LOCAL`,
      )
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getCuratedPackageSteeringSummary(params, successCallback, errorCallback) {
    const { id, start, end, resolution } = params;
    this.axiosInstance
      .get(`${curatedPackagesEndpoint}${id}/steeringsummary`, {
        params: {
          start_date: start,
          end_date: end,
          timezone: 'UNIT_LOCAL',
          resolution: resolution,
        },
      })
      .then(
        (successResponse) => {
          successCallback(SpendSteeringSummary.fromAPI(successResponse.data, start, end, resolution));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getPackageSummaryStats(params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`dashboards/packages/${params.id}/summary/${params.start}/${params.end}?timezone=UNIT_LOCAL`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getPackageDefaultRTBMetrics(packageId, params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`${packageEndpoint}metrics/${packageId}`, {
        params: {
          start: Math.round(params.start.toMillis() / 1000),
          end: Math.round(params.end.toMillis() / 1000),
          keep_series: params.keep_series,
        },
      })
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getPackageBiddingMetrics(params, successCallback, errorCallback) {
    this.axiosInstance
      .get(`${packageEndpoint}bidding/metrics?${params.packageIDs.map((x) => `package_id=${x}`).join('&')}`)
      .then(
        (successResponse) => {
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  postPackage(pack, successCallback, errorCallback) {
    this.axiosInstance.post(packagesEndpoint, [pack.toApi()]).then(
      (successResponse) => {
        successCallback(Package.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putPackage(pack, successCallback, errorCallback) {
    const endpoint = pack.packageType === PackageType.CURATED ? curatedPackagesEndpoint : packagesEndpoint;
    this.axiosInstance.put(`${endpoint}${pack.id}/`, pack.toApi ? pack.toApi() : pack).then(
      (successResponse) => {
        successCallback(Package.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putPackageState(pack, successCallback, errorCallback) {
    const endpoint = pack.packageType === PackageType.CURATED ? curatedPackagesEndpoint : packagesEndpoint;
    this.axiosInstance.put(`${endpoint}${pack.id}/`, pack.toApiStateChange()).then(
      (successResponse) => {
        successCallback(Package.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  postConvertPackageToLineitems(pack, successCallback, errorCallback) {
    this.axiosInstance.post(`${packagesEndpoint}${pack.id}/${packagesToLineitemsEndpoint}`, [pack.toApi()]).then(
      (successResponse) => {
        successCallback(LineItem.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  // Deal Discovery
  async getPackagesMatchingTargeting(payload, successCallback, errorCallback) {
    this.axiosInstance.post(dealDiscoveryEndpoint, payload).then(
      (successResponse) => {
        successCallback(DealDiscoveryStatistics.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // PreSaleHeatmaps
  //
  deletePreSaleHeatmap(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${preSaleHeatmapsEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getPreSaleHeatmaps(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: preSaleHeatmapsEndpoint,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(ApiUtils.getResult(successResponse, PreSaleHeatmapApiModel));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  getPreSaleHeatmap(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${preSaleHeatmapsEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(PreSaleHeatmapApiModel.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getPublicPreSaleHeatmap(uuid, password, successCallback, errorCallback) {
    this.axiosInstancePublicEndpoints.get(`${preSaleHeatmapsEndpoint}uuid/${uuid}?password=${password}`).then(
      (successResponse) => {
        successCallback(PreSaleHeatmapApiModel.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  postPreSaleHeatmap(preSaleHeatmap, successCallback, errorCallback) {
    const payload = preSaleHeatmap.toApi();
    this.axiosInstance.post(preSaleHeatmapsEndpoint, [payload]).then(
      (successResponse) => {
        successCallback(PreSaleHeatmapApiModel.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putPreSaleHeatmap(preSaleHeatmap, successCallback, errorCallback) {
    this.axiosInstance.put(`${preSaleHeatmapsEndpoint}${preSaleHeatmap.id}/`, preSaleHeatmap.toApi()).then(
      (successResponse) => {
        successCallback(PreSaleHeatmapApiModel.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // Reports
  //
  postReportSummaries(data, successCallback, errorCallback) {
    // mediaowner_ids = data['mediaowner_ids'] if 'mediaowner_ids' in data else None
    // buyer_ids = data['buyer_ids'] if 'buyer_ids' in data else None
    // advertiser_ids = data['advertiser_ids'] if 'advertiser_ids' in data else None
    // dsp_ids = data['dsp_ids'] if 'dsp_ids' in data else None
    // package_ids = data['package_ids'] if 'package_ids' in data else None
    // lineitem_ids = data['lineitem_ids'] if 'lineitem_ids' in data else None

    const queryString = data.customQueryParams?.include_child_orgs
      ? `?include_child_orgs=${data.customQueryParams.include_child_orgs}`
      : '';

    this.axiosInstance
      .request({
        url: `${reportsEndpoint}summaries${queryString}`,
        method: 'post',
        data,
        cancelToken: data.cancelTokenSource != null ? data.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) {
            // cancelled
            return;
          }
          const summarizedReport = SummarizedReport.fromApi(successResponse.data);
          successCallback(summarizedReport);
        },
        (errorResponse) => {
          errorCallback(errorResponse);
        },
      );
  }

  postReportSummaryGroupings(data, successCallback, errorCallback) {
    // mediaowner_ids = data['mediaowner_ids'] if 'mediaowner_ids' in data else None
    // buyer_ids = data['buyer_ids'] if 'buyer_ids' in data else None
    // advertiser_ids = data['advertiser_ids'] if 'advertiser_ids' in data else None
    // dsp_ids = data['dsp_ids'] if 'dsp_ids' in data else None
    // package_ids = data['package_ids'] if 'package_ids' in data else None
    // lineitem_ids = data['lineitem_ids'] if 'lineitem_ids' in data else None

    const queryString = data.customQueryParams?.include_child_orgs
      ? `?include_child_orgs=${data.customQueryParams.include_child_orgs}`
      : '';

    this.axiosInstance
      .request({
        url: `${reportsEndpoint}summarygroupings${queryString}`,
        method: 'post',
        data,
        cancelToken: data.cancelTokenSource != null ? data.cancelTokenSource.token : null,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) {
            // cancelled
            return;
          }
          const summarizedReport = SummarizedReport.fromApi(successResponse.data);
          successCallback(summarizedReport);
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  getReportSummaryActiveFilters(
    { fromDate, toDate, includeChildOrgs, cancelTokenSource },
    successCallback,
    errorCallback,
  ) {
    this.axiosInstance
      .request({
        url: `${reportsEndpoint}summaries/activefilters`,
        method: 'get',
        params: {
          from_date: fromDate,
          to_date: toDate,
          include_child_orgs: includeChildOrgs,
        },
        cancelToken: cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse == null) {
            // cancelled.
            return;
          }
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  //
  // ReportDefinitions
  //
  async runReportDefinition({ id, params = {}, cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .request({
        url: `${reportsEndpoint}fromdefinition/${id}`,
        method: 'get',
        params,
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ReportExecution.fromApi(response.data);
  }

  //
  // Sites
  //
  deleteSite(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${sitesEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getSite({ id, expandUnits }, successCallback, errorCallback) {
    const params = expandUnits ? { $expand: Site.expands.join(',') } : { $expand: 'location' };
    this.axiosInstance.get(`${sitesEndpoint}${id}`, { params }).then(
      (successResponse) => {
        successCallback(Site.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getSitesMinimal(queryParams) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${sitesEndpoint}minimal`,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async getSiteAudit({ id, cancelTokenSource = null }) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${sitesEndpoint}${id}/audit`,
        method: 'get',
        cancelToken: cancelTokenSource != null ? cancelTokenSource.token : null,
      })
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });

    return new AuditTrail(successResponse.data, 'Site');
  }

  async getSitesBriefUsingPost(siteIDs, unitIDs = null, networkIDs = null): Promise<Array<SiteInfo>> {
    const body: Record<string, number[]> = {
      site_ids: siteIDs,
      unit_ids_filter: unitIDs,
    };
    if (networkIDs !== null) {
      body.network_ids_filter = networkIDs;
    }
    const successResponse = await this.axiosInstance.post(`${sitesEndpoint}brief`, body).catch((errorResponse) => {
      throw ApiUtils.getError(errorResponse);
    });
    return successResponse.data.map((x) => SiteInfo.fromApi(x));
  }

  getLocationSites(location, networkIDs, ownerIDs, successCallback, errorCallback) {
    const params = {
      network_ids: (networkIDs || []).join(','),
      owner_ids: (ownerIDs || []).join(','),
    };
    this.axiosInstance
      .request({
        url: `${sitesEndpoint}location/${location.id}`,
        params,
      })
      .then(
        (successResponse) => {
          successCallback(successResponse.data.map((x) => Site.fromApi(x)));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  postSite(site, successCallback, errorCallback) {
    this.axiosInstance.post(sitesEndpoint, [site.toApi()]).then(
      (successResponse) => {
        successCallback(Site.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putSite(site, successCallback, errorCallback) {
    this.axiosInstance.put(`${sitesEndpoint}${site.id}/`, site.toApi ? site.toApi() : site).then(
      (successResponse) => {
        successCallback(Site.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // Subscriptions
  //
  async getOrganizationSubscriptions({ cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${subscriptionsEndpoint}organizationsubscriptions`, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw getErrorOld(err);
      });
    if (response == null) {
      return null;
    }
    return response.data.map((x) => OrganizationSubscription.fromApi(x));
  }

  subscribeToOrganizationSubscriptions(subscriptionType, successCallback, errorCallback) {
    const url = `${subscriptionsEndpoint}organizationsubscriptions/newsubscription`;
    const body = {
      subscription_type: subscriptionType,
    };
    this.axiosInstance.post(url, body).then(
      () => {
        successCallback();
      },
      (errorResponse) => {
        errorCallback(errorResponse);
        console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
      },
    );
  }

  deleteOrganizationSubscription(subscriptionID, successCallback, errorCallback) {
    const url = `${subscriptionsEndpoint}organizationsubscriptions/${subscriptionID}`;
    this.axiosInstance.delete(url).then(
      () => {
        successCallback();
      },
      (errorResponse) => {
        errorCallback(errorResponse);
        console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
      },
    );
  }

  //
  // Targeting Criteria
  //
  async getTargetingCriteria({ cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(targetingCriteriaEndpoint, {
        params: {
          $expand: 'targetingcriterion_subtypes',
        },
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw getErrorOld(err);
      });
    if (response == null) {
      return null;
    }
    return response.data.map((x) => TargetingCriterion.fromApi(x));
  }

  //
  // Units
  //
  deleteUnit(id, successCallback, errorCallback) {
    this.axiosInstance.delete(`${unitsEndpoint}${id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getUnit(id, successCallback, errorCallback) {
    this.axiosInstance.get(`${unitsEndpoint}${id}?$expand=${Unit.expands.join(',')}`).then(
      (successResponse) => {
        successCallback(Unit.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  getUnitsBrief(unitIds, successCallback, errorCallback) {
    this.axiosInstance.get(`${unitsEndpoint}brief?unit_ids=${unitIds.join(',')}`).then(
      (successResponse) => {
        successCallback(successResponse.data.map((x) => UnitInfo.fromApi(x)));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async getUnitsBriefUsingPost(unitIDs) {
    const body = {
      unit_ids: unitIDs,
    };
    const successResponse = await this.axiosInstance.post(`${unitsEndpoint}brief`, body).catch((errorResponse) => {
      throw ApiUtils.getError(errorResponse);
    });
    const units = successResponse?.data || [];
    const unitsCount = units?.length;
    const batchSize = 750;
    let response: Array<UnitInfo> = [];

    if (unitsCount > 0) {
      const getBatch = async (page, acc = []) => {
        if (unitsCount / batchSize < page) {
          return acc;
        }
        const items = units.slice(page * batchSize, (page + 1) * batchSize).map((x) => UnitInfo.fromApi(x));
        return getBatch(page + 1, [...acc, ...items]);
      };
      response = await getBatch(0);
    }

    return response;
  }

  async getUnitAudienceData(params) {
    const { id, fromDate, toDate } = params;

    const successResponse = await this.axiosInstance
      .get(`${unitsEndpoint}${id}/audiencedata/${toServerDate(fromDate)}/${toServerDate(toDate)}`)
      .catch((errorResponse) => {
        throw ApiUtils.getError(errorResponse);
      });
    return successResponse.data;
  }

  async getUnitsAudienceData({
    ids,
    fromDate,
    toDate,
    demographicIDs,
    cancelTokenSource = null,
    progressCallback = null,
  }) {
    const endpoint = `${unitsEndpoint}audiencedata/${toServerDate(fromDate)}/${toServerDate(toDate)}`;
    const batchSize = 250;
    const batches = [];
    for (let i = 0; i < ids.length; i += batchSize) {
      batches.push(ids.slice(i, i + batchSize));
    }

    let result = [];
    const maxParallelQueries = 3;
    const processNextQueryBatch = async (batchIndex = 0) => {
      if (progressCallback != null) progressCallback(batchIndex * maxParallelQueries, batches.length);
      const batchPromises = batches
        .slice(batchIndex * maxParallelQueries, (batchIndex + 1) * maxParallelQueries)
        .map((batch) =>
          this.axiosInstance.request({
            method: 'POST',
            url: endpoint,
            data: {
              unit_ids: batch,
              demographic_ids: demographicIDs,
            },
            cancelToken: cancelTokenSource != null ? cancelTokenSource.token : undefined,
          }),
        );
      const batchResponses = await Promise.all(batchPromises);
      const batchResults = batchResponses.map((x) => x.data);
      batchResults.forEach((br) => {
        result = { ...result, ...br };
      });
      if (batchIndex * maxParallelQueries < batches.length) {
        await processNextQueryBatch(batchIndex + 1);
      }
    };

    await processNextQueryBatch();
    return result;
  }

  async getUnitsMinimal(queryParams) {
    const successResponse = await this.axiosInstance
      .request({
        url: `${unitsEndpoint}minimal`,
        params: queryParams.toObject(),
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async getClusteredUnits({ queryParams, minLat, maxLat, minLng, maxLng, zoom, cancelTokenSource, unitIDs }) {
    const config = {
      url: `${unitsEndpoint}clustered`,
      method: 'POST',
      data: unitIDs != null ? { unit_ids: unitIDs } : {},
      params: queryParams.toObject(),
      cancelToken: cancelTokenSource.token,
      min_lat: minLat,
      max_lat: maxLat,
      min_lon: minLng,
      max_lon: maxLng,
      zoom_level: zoom,
    };
    const successResponse = await this.axiosInstance.request(config).catch((err) => {
      throw ApiUtils.getError(err);
    });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async getClusteredUnitsFromTargets({
    params,
    minLat,
    maxLat,
    minLng,
    maxLng,
    zoom,
    cancelTokenSource,
    unitIDs,
    type,
    isPrivate = true,
    planUuid = null,
    withUnitIds = false,
    withMediaOwnerInformation = false,
  }) {
    const serializedParams = JSON.stringify(params);
    const isPlan = params.type === EntityType.PLANS;
    const isPublicPlan = isPlan && !isPrivate;
    const endPoint = isPlan ? plansEndpoint : lineItemsEndpoint;
    let cachedFilters = APIProxy.unitFiltersForTargetsCache.find((x) => x.key === serializedParams);
    // https://hivestack.atlassian.net/browse/CAM-4392 No need for the frontend to do this call with Public APIs
    if ((cachedFilters == null || APIProxy.typeFiltersForTargetsCache !== type) && !isPublicPlan) {
      if (APIProxy.typeFiltersForTargetsCache !== type) {
        cachedFilters = null;
      }
      const unitFiltersResponse = await this.axiosInstance
        .request({
          url: `${endPoint}unitfiltersfortargets`,
          method: 'POST',
          data: params,
          cancelToken: cancelTokenSource.token,
        })
        .catch((err) => {
          throw ApiUtils.getError(err);
        });
      if (unitFiltersResponse == null) {
        return null;
      }
      cachedFilters = {
        key: serializedParams,
        filters: unitFiltersResponse.data.length > 0 ? unitFiltersResponse.data[0] : null,
      };
      APIProxy.unitFiltersForTargetsCache.push(cachedFilters);
      APIProxy.typeFiltersForTargetsCache = type;
    }
    const cachedFiltersValue = isPrivate ? cachedFilters.filters : null;
    const decimal = 10 ** 9; // Round down coords to the 9th decimal to exploit the cache
    const unitsSettings = {
      url: isPublicPlan ? `/plans/${planUuid}/unitsclustered` : `${unitsEndpoint}clustered`,
      method: 'POST',
      params: {
        min_lat: Math.floor(minLat * decimal) / decimal,
        max_lat: Math.floor(maxLat * decimal) / decimal,
        min_lon: Math.floor(minLng * decimal) / decimal,
        max_lon: Math.floor(maxLng * decimal) / decimal,
        zoom_level: zoom,
        with_unit_ids: withUnitIds, // when false, BE manages the logic and will know when to send unit_ids : when the cluster is unzoomable, meaning when the min_lon = max_lon and min_lat = max_lat
        with_mediaowner_information: withMediaOwnerInformation,
      },
      data: {
        ...(unitIDs != null && { unit_ids: unitIDs }),
        $filter: cachedFiltersValue ? [cachedFiltersValue] : null,
      },
      cancelToken: cancelTokenSource.token,
    };

    // @doc How to use https://hivestack.atlassian.net/browse/CAM-5108
    const sitesSettings = {
      url: `${sitesEndpoint}clustered`,
      method: 'POST',
      data: {
        min_lat: Math.floor(minLat * decimal) / decimal,
        max_lat: Math.floor(maxLat * decimal) / decimal,
        min_lon: Math.floor(minLng * decimal) / decimal,
        max_lon: Math.floor(maxLng * decimal) / decimal,
        zoom_level: zoom,
        unit_filter: cachedFiltersValue,
      },
      cancelToken: cancelTokenSource.token,
    };
    const selectedSettings = type === 'sites' ? sitesSettings : unitsSettings;
    const successResponse = await this.axiosInstance.request(selectedSettings as unknown).catch((err) => {
      throw ApiUtils.getError(err);
    });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  /**
   * @doc How to use https://hivestack.atlassian.net/browse/CAM-5108
   */
  async getClusteredSiteNetworksFromTargets({
    params,
    minLat,
    maxLat,
    minLng,
    maxLng,
    zoom,
    cancelTokenSource,
    type,
    isPrivate = true,
    planUuid = null,
  }) {
    const serializedParams = JSON.stringify(params);
    const isPlan = params.type === EntityType.PLANS;
    const isPublicPlan = isPlan && !isPrivate;
    const endPoint = params.type === EntityType.PLANS ? plansEndpoint : lineItemsEndpoint;
    let cachedFilters = APIProxy.unitFiltersForTargetsCache.find((x) => x.key === serializedParams);
    // https://hivestack.atlassian.net/browse/CAM-4392 No need for the frontend to do this call with Public APIs
    if ((cachedFilters == null || APIProxy.typeFiltersForTargetsCache !== type) && !isPublicPlan) {
      if (APIProxy.typeFiltersForTargetsCache !== type) {
        cachedFilters = null;
      }
      const unitFiltersResponse = await this.axiosInstance
        .request({
          url: `${endPoint}unitfiltersfortargets`,
          method: 'POST',
          data: params,
          cancelToken: cancelTokenSource.token,
        })
        .catch((err) => {
          throw ApiUtils.getError(err);
        });
      if (unitFiltersResponse == null) {
        return null;
      }
      cachedFilters = {
        key: serializedParams,
        filters: unitFiltersResponse.data.length > 0 ? unitFiltersResponse.data[0] : null,
      };
      APIProxy.unitFiltersForTargetsCache.push(cachedFilters);
      APIProxy.typeFiltersForTargetsCache = type;
    }
    const cachedFiltersValue = isPrivate ? cachedFilters.filters : null;
    const decimal = 10 ** 9; // Round down coords to the 9th decimal to exploit the cache
    const sitesNetworksSettings = {
      url: isPublicPlan ? `/plans/${planUuid}/sitenetworksclustered` : sitesNetworkEndpoint,
      method: 'POST',
      data: {
        min_lat: Math.floor(minLat * decimal) / decimal,
        max_lat: Math.floor(maxLat * decimal) / decimal,
        min_lon: Math.floor(minLng * decimal) / decimal,
        max_lon: Math.floor(maxLng * decimal) / decimal,
        zoom_level: zoom,
        unit_filter: cachedFiltersValue ?? null,
      },
      cancelToken: cancelTokenSource.token,
    };

    const successResponse = await this.axiosInstance.request(sitesNetworksSettings).catch((err) => {
      throw ApiUtils.getError(err);
    });

    if (successResponse == null) {
      return null;
    }
    return successResponse.data;
  }

  async getUnitsHourlyImpressions(params) {
    const { isPrivate = true, planUuid = null } = params;
    try {
      if (!isPrivate && !planUuid) {
        throw new Error("Can't get hourly impressions for units for a public shared plan without a plan id");
      }

      const endpoint = isPrivate
        ? unitsHourlyImpressionsEndpoint
        : `/plans/${planUuid}/audiencedataforperiodhourlyimpressions`;
      const response = await this.axiosInstance.post(endpoint, params);
      if (response == null) {
        return null;
      }
      return response.data;
    } catch (err) {
      throw getErrorOld(err);
    }
  }

  postUnit(unit, successCallback, errorCallback) {
    this.axiosInstance.post(unitsEndpoint, [unit.toApi()]).then(
      (successResponse) => {
        successCallback(Unit.fromApi(successResponse.data[0]));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  putUnit(unit, successCallback, errorCallback) {
    this.axiosInstance.put(`${unitsEndpoint}${unit.id}/`, unit.toApi ? unit.toApi() : unit).then(
      (successResponse) => {
        successCallback(Unit.fromApi(successResponse.data));
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  async putUnitImage(unitId, unitImage) {
    const data = new FormData();
    data.append('unitImage', unitImage);
    const response = await this.axiosInstance.post(`${unitsEndpoint}${unitId}/image`, data).catch((err) => {
      throw getErrorOld(err);
    });
    if (response == null) {
      return null;
    }
    return response.data;
  }

  async getAllUnitDimensions({ cancelTokenSource = null }) {
    const response = await this.axiosInstance
      .get(`${unitsEndpoint}getalldimensions`, {
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw getErrorOld(err);
      });
    if (response == null) {
      return null;
    }
    return response.data;
  }

  getCreativesPerUnit(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${unitsEndpoint}${queryParams.parentID}/creatives`,
        params: queryParams !== null ? queryParams.toObject() : undefined,
        cancelToken: queryParams.cancelTokenSource.token,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          const creatives = ApiUtils.getResult(successResponse, Creative);
          successCallback(creatives);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  //
  // Unit Packs
  //
  getUnitsInUnitPack(queryParams, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${unitPacksEndpoint}${queryParams.id}/units`,
        params: queryParams.toObject(),
      })
      .then(
        (successResponse) => {
          successCallback(ApiUtils.getResult(successResponse, Unit));
        },
        (errorResponse) => {
          errorCallback(errorResponse);
          console.error(JSON.stringify(errorResponse.response.data));
        },
      );
  }

  async postUnitsInUnitPack({ id, data }) {
    const response = await this.axiosInstance
      .request({
        url: `${unitPacksEndpoint}${id}/units/`,
        method: 'post',
        data,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return response.data;
  }

  //
  // UnitProperties
  //
  postUnitProperty(data, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${unitPropertiesEndpoint}`,
        method: 'post',
        data,
      })
      .then(
        (successResponse) => {
          successCallback(successResponse);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  putUnitProperty(id, data, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${unitPropertiesEndpoint}${id}`,
        method: 'put',
        data,
      })
      .then(
        (successResponse) => {
          successCallback(successResponse);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  deleteUnitProperty(id, successCallback, errorCallback) {
    this.axiosInstance
      .request({
        url: `${unitPropertiesEndpoint}${id}`,
        method: 'delete',
      })
      .then(
        (successResponse) => {
          successCallback(successResponse);
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  //
  // UnitFallbackCreatives
  //
  checkUnitFallbackCreativesEndpoint(successCallback, errorCallback) {
    this.axiosInstance.options(unitFallbackCreativesEndpoint).then(
      () => {
        successCallback();
      },
      () => {
        errorCallback();
      },
    );
  }

  postUnitFallbackCreatives(unitFallbackCreatives, successCallback, errorCallback) {
    this.axiosInstance
      .post(
        unitFallbackCreativesEndpoint,
        unitFallbackCreatives.map((x) => x.toApi()),
      )
      .then(
        (successResponse) => {
          successCallback(successResponse.data.map((x) => UnitFallbackCreative.fromApi(x)));
        },
        (errorResponse) => {
          handleError(errorResponse, errorCallback);
        },
      );
  }

  updateUnitFallbackCreatives(unitFallbackCreatives) {
    const toUpdate = unitFallbackCreatives.reduce((mem, x) => {
      const apiModel = x.toApi();
      const mutatedMem = { ...mem };
      mutatedMem[`${apiModel.unitfallbackcreative_id}`] = apiModel;
      return mutatedMem;
    }, {});

    const payload = {
      to_update: toUpdate,
    };

    return this.axiosInstance
      .patch(`${unitFallbackCreativesEndpoint}?$expandstrict=[]`, payload)
      .then((successResponse) => successResponse.data.updated_entities.map((x) => UnitFallbackCreative.fromApi(x)))
      .catch((errorResponse) => {
        console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        throw getErrorOld(errorResponse);
      });
  }

  deleteUnitFallbackCreatives(unitFallbackCreative, successCallback, errorCallback) {
    this.axiosInstance.delete(`${unitFallbackCreativesEndpoint}${unitFallbackCreative.id}`).then(
      (successResponse) => {
        successCallback(successResponse.data);
      },
      (errorResponse) => {
        handleError(errorResponse, errorCallback);
      },
    );
  }

  //
  // User things
  //
  changePassword(currentPassword, newPassword, successCallback, errorCallback) {
    this.axiosInstance
      .put(`${usersEndpoint}changepassword`, {
        currentPassword: currentPassword,
        password: newPassword,
      })
      .then(
        (successResponse) => {
          if (successResponse === undefined) {
            return;
          }
          successCallback(successResponse.data);
        },
        (errorResponse) => {
          errorCallback(errorResponse.response.data);
          console.error(errorResponse.response != null ? JSON.stringify(errorResponse.response.data) : 'Unknown error');
        },
      );
  }

  async createUser(email, roleID) {
    const response = await this.axiosInstance
      .post(`${organizationsEndpoint}users`, {
        email,
        role_id: roleID,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });

    return response.data;
  }

  async getRoles({ queryParams = QueryParams.all(), cancelTokenSource = null } = {}) {
    const response = await this.axiosInstance
      .get(rolesEndpoint, {
        params: queryParams.toObject(),
        cancelToken: cancelTokenSource ? cancelTokenSource.token : null,
      })
      .catch((err) => {
        throw ApiUtils.getError(err);
      });
    if (response == null) {
      return null;
    }
    return ApiUtils.getResult(response, Role);
  }

  //
  // Postal codes files
  //
  async uploadPostalCodesFile({ name, file }) {
    const data = new FormData();
    data.append('name', name);
    data.append('file', file);

    const successResponse = await this.axiosInstance.post(`${postalCodesFilesEndpoint}upload`, data).catch((err) => {
      throw ApiUtils.getError(err);
    });
    return successResponse.data;
  }
}

export default APIProxy;
